"use client";
import { createContext as e, memo as t, useCallback as n, useContext as r, useEffect as i, useId as a, useLayoutEffect as o, useMemo as s, useRef as c, useState as l, useSyncExternalStore as u } from "react";
import { Fragment as d, jsx as f, jsxs as p } from "react/jsx-runtime";
import { animate as m, motion as h, useMotionValue as g, useTransform as _ } from "motion/react";
import { uniformInt as v } from "pure-rand/distribution/uniformInt";
import { xoroshiro128plus as y } from "pure-rand/generator/xoroshiro128plus";
import { QueryClient as b, useMutation as x, useQuery as S } from "@tanstack/react-query";
//#region src/core/currency.ts
var C = {
	name: "credit",
	namePlural: "credits",
	symbol: "$",
	multiplier: 100,
	iconSvg: ""
};
function w(e) {
	switch (e) {
		case 1: return 0;
		case 10: return 1;
		case 100: return 2;
		case 1e3: return 3;
	}
}
var T = [
	0,
	1,
	2,
	3
].map((e) => new Intl.NumberFormat("en-US", {
	minimumFractionDigits: e,
	maximumFractionDigits: e
}));
function E(e, t, n = {}) {
	if (!Number.isFinite(e)) throw RangeError(`formatAmount: amount is not a finite number of cents: ${e}`);
	let r = w(t.multiplier), i = Math.abs(e) / t.multiplier, a = e < 0 ? "-" : "", o = n.compact && i >= 1e3 ? `${(i / 1e3).toFixed(1)}k` : T[r].format(i);
	return `${a}${t.symbol}${o}`;
}
function D(e, t) {
	return e === t.multiplier ? t.name : t.namePlural;
}
function O(e, t) {
	let n = E(e, t);
	return t.symbol === "" ? `${n} ${D(e, t)}` : n;
}
//#endregion
//#region src/i18n/strings.ts
var k = {
	open: "Open",
	openAgain: "Open again",
	skip: "Skip",
	quantity: "Quantity",
	quickReveal: "Quick reveal",
	claim: "Claim items",
	claiming: "Claiming…",
	sellBack: "Sell back",
	sellingBack: "Selling back…",
	soldBackFor: "Sold back for",
	errorSellBackFailed: "Selling back failed. Please try again.",
	sellItem: "Sell",
	sellingItem: "Selling…",
	soldItemFor: "Sold for",
	withdrawItem: "Withdraw",
	withdrawRequested: "Withdrawal started",
	errorSellItemFailed: "Selling this item failed. Please try again.",
	limitedItemNotice: "Limited item. If you cannot withdraw it, you keep it and can sell it for its value.",
	substituteBadge: "Cannot be shipped",
	autoClaimLabel: "Auto-claim future opens",
	errorClaimFailed: "Claiming failed. Please try again.",
	totalValue: "Total value",
	loading: "Loading…",
	demoBadge: "Demo",
	errorGeneric: "Something went wrong. Please try again.",
	errorInsufficientBalance: "Not enough balance to open this box.",
	errorAccountLocked: "This account is locked. Contact support.",
	provablyFair: "Provably fair",
	serverSeedHash: "Server seed hash",
	clientSeed: "Client seed",
	salt: "Salt",
	seedDate: "Seed date",
	nonce: "Nonce",
	result: "Result",
	noOpensYet: "Open the box to record provably-fair rolls here.",
	battleStartsIn: "Battle starts in",
	battleGoodLuck: "Good luck!",
	battleGo: "GO",
	battleRound: "Round"
};
function A(e) {
	return e ? {
		...k,
		...e
	} : k;
}
//#endregion
//#region src/theme/rarity.ts
var j = {
	0: "179,179,179",
	1: "34,197,94",
	2: "59,130,246",
	3: "168,85,247",
	4: "249,115,22",
	5: "249,115,22"
}, M = {
	0: "#A9A9CA",
	1: "#22C55E",
	2: "#3B82F6",
	3: "#A855F7",
	4: "#FFC700",
	5: "#FFC700"
}, ee = "#FFFFFF";
function N(e, t, n, r) {
	return `drop-shadow(0 0 ${n} rgba(${e[t] || "255,255,255"},${r}))`;
}
function te(e, t) {
	switch (t) {
		case 0: return N(e, t, "15px", "0.3");
		case 1: return N(e, t, "15px", "0.4");
		case 2: return N(e, t, "15px", "0.7");
		case 3:
		case 4:
		case 5: return N(e, t, "15px", "1");
		default: return N(e, t, "15px", "1");
	}
}
function P(e, t) {
	return t == null ? ee : e[t] ?? "#FFFFFF";
}
//#endregion
//#region src/theme/theme.ts
var F = {
	background: "#0e0f1a",
	surface: "#131420",
	border: "#2a2b3d",
	text: "#ffffff",
	textMuted: "#a9a9ca",
	accent: "#8b5cf6",
	accentForeground: "#ffffff"
};
function I(e) {
	let t = e.trim();
	if (/^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/.test(t)) return t.replace(/\s+/g, "");
	let n = t.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
	if (n) {
		let e = n.length === 3 ? n.replace(/./g, (e) => e + e) : n;
		return `${Number.parseInt(e.slice(0, 2), 16)},${Number.parseInt(e.slice(2, 4), 16)},${Number.parseInt(e.slice(4, 6), 16)}`;
	}
	let r = t.match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i);
	return r ? `${r[1]},${r[2]},${r[3]}` : null;
}
function L(e) {
	let [t = 0, n = 0, r = 0] = e.split(",").map((e) => Number.parseInt(e, 10)), i = (e) => Math.max(0, Math.min(255, e)).toString(16).padStart(2, "0");
	return `#${i(t)}${i(n)}${i(r)}`;
}
var ne = [
	0,
	1,
	2,
	3,
	4,
	5
];
function re(e) {
	let t = {
		...F,
		...e?.colors
	}, n = { ...j }, r = { ...M };
	for (let t of ne) {
		let i = e?.rarity?.[t];
		if (!i) continue;
		let a = I(i);
		a && (n[t] = a, r[t] = L(a));
	}
	return {
		cssVars: {
			"--los-bg": t.background,
			"--los-surface": t.surface,
			"--los-border": t.border,
			"--los-text": t.text,
			"--los-text-muted": t.textMuted,
			"--los-accent": t.accent,
			"--los-accent-foreground": t.accentForeground,
			"--los-radius": e?.radius ?? "16px",
			"--los-font": e?.fontFamily ?? "inherit"
		},
		colors: t,
		rarityRgb: n,
		railHex: r
	};
}
//#endregion
//#region src/react/LootBoxContext.tsx
var R = () => {}, z = {
	strings: k,
	theme: re(),
	sounds: {
		playTickSound: R,
		playRaritySound: R,
		unlock: R
	},
	currency: C
}, ie = e(z);
function B() {
	return r(ie);
}
//#endregion
//#region src/react/LootConfigContext.ts
var V = e({});
function H() {
	return r(V);
}
//#endregion
//#region src/sounds/SoundPlayer.ts
var ae = class {
	context = null;
	gainNode = null;
	buffers = /* @__PURE__ */ new Map();
	volumeValue = 1;
	get volume() {
		return this.volumeValue;
	}
	setVolume(e) {
		this.volumeValue = Math.max(0, Math.min(1, e)), this.gainNode && (this.gainNode.gain.value = this.volumeValue);
	}
	ensureContext() {
		if (this.context) return this.context;
		if (typeof window > "u") return null;
		let e = window.AudioContext ?? window.webkitAudioContext;
		if (!e) return null;
		try {
			this.context = new e(), this.gainNode = this.context.createGain(), this.gainNode.gain.value = this.volumeValue, this.gainNode.connect(this.context.destination);
		} catch {
			this.context = null, this.gainNode = null;
		}
		return this.context;
	}
	unlock() {
		let e = this.ensureContext();
		e && e.state === "suspended" && e.resume().catch(() => {});
	}
	preload(e) {
		this.getBuffer(e);
	}
	getBuffer(e) {
		let t = this.buffers.get(e);
		if (t) return t;
		let n = this.ensureContext(), r = n ? fetch(e).then((e) => e.ok ? e.arrayBuffer() : null).then((e) => e ? n.decodeAudioData(e) : null).catch(() => null) : Promise.resolve(null);
		return this.buffers.set(e, r), r;
	}
	play(e, t) {
		if (!e) return;
		let n = this.ensureContext();
		!n || !this.gainNode || this.getBuffer(e).then((e) => {
			if (!(!e || !this.gainNode)) try {
				let r = n.createBufferSource();
				if (r.buffer = e, t?.volume !== void 0) {
					let e = n.createGain();
					e.gain.value = Math.max(0, Math.min(1, t.volume)), r.connect(e), e.connect(this.gainNode);
				} else r.connect(this.gainNode);
				r.start();
			} catch {}
		});
	}
	dispose() {
		this.buffers.clear(), this.context?.close().catch(() => {}), this.context = null, this.gainNode = null;
	}
}, oe = {
	tick: "tick.mp3",
	badPrize: "bad-prize.mp3",
	rarePrize: "rare-prize.mp3",
	megaPrize: "mega-prize.mp3"
};
function U(e, t) {
	if (e === !1) return null;
	let n = t?.replace(/\/$/, ""), r = {
		...n ? {
			tick: `${n}/${oe.tick}`,
			badPrize: `${n}/${oe.badPrize}`,
			rarePrize: `${n}/${oe.rarePrize}`,
			megaPrize: `${n}/${oe.megaPrize}`
		} : {},
		...e
	};
	return Object.values(r).some(Boolean) ? r : null;
}
function se({ sounds: e, soundBaseUrl: t }) {
	let n = c(null), r = s(() => U(e, t), [e, t]), a = c(r);
	return a.current = r, i(() => () => {
		n.current?.dispose(), n.current = null;
	}, []), s(() => {
		let e = () => (n.current ||= new ae(), n.current);
		return {
			playTickSound: () => {
				let t = a.current;
				t?.tick && e().play(t.tick);
			},
			playRaritySound: (t) => {
				let n = a.current;
				n && (t === 2 || t === 3 ? e().play(n.rarePrize) : t >= 4 ? e().play(n.megaPrize) : e().play(n.badPrize));
			},
			unlock: () => {
				if (!a.current) return;
				let t = e();
				t.unlock();
				for (let e of Object.values(a.current)) e && t.preload(e);
			}
		};
	}, []);
}
//#endregion
//#region src/utils/cx.ts
function W(...e) {
	return e.filter(Boolean).join(" ");
}
//#endregion
//#region src/battle/BattlesIcon.tsx
function ce({ size: e = 16, className: t }) {
	return /* @__PURE__ */ f("svg", {
		"aria-hidden": "true",
		className: t,
		width: e,
		height: e,
		viewBox: "0 0 40 40",
		fill: "currentColor",
		xmlns: "http://www.w3.org/2000/svg",
		children: /* @__PURE__ */ f("path", { d: "M25.156 2.703c-.083.11-.902 1.18-1.829 2.382-.923 1.197-1.972 2.564-2.334 3.03-.356.466-.757.98-.883 1.14l-.235.297-3.361.008-3.361.013.849.431c.465.236 1.245.632 1.728.88l.875.457-.235.553c-.13.305-.266.61-.296.684-.035.07-.222.513-.418.979s-.588 1.389-.87 2.046c-.284.658-.615 1.441-.74 1.742-.127.3-.275.64-.327.762a45 45 0 0 0-.275.63c-.418.99-1.058 2.504-1.532 3.614-.279.658-.523 1.237-.54 1.28-.013.044-.04.07-.052.061-.013-.013 0-1.119.022-2.455.143-7.532.165-9.495.113-9.548a8 8 0 0 0-.675-.322c-.348-.157-1.102-.492-1.676-.749-2.094-.936-2.386-1.066-2.408-1.066-.026 0 .035 12.782.074 15.412l.026 1.806h7.662l.144-.283c.079-.156.388-.831.693-1.502.635-1.415 1.558-3.46 1.785-3.962.087-.191.23-.509.326-.71.092-.2.166-.37.166-.378s.056-.14.126-.292.187-.413.265-.579c.079-.17.214-.47.305-.674.274-.619 1.602-3.57 1.842-4.093.126-.274.614-1.367.857-1.915.179-.405.388-.867.871-1.938.174-.383.4-.884.5-1.11s.414-.923.697-1.546l.793-1.763c.157-.348.33-.74.392-.87.06-.131.274-.614.479-1.067.204-.457.448-.993.54-1.193.19-.414.186-.396.117-.392-.03 0-.122.092-.2.2M24.32 13.187c-.832.091-1.472.257-1.981.51-.41.208-.958.787-1.206 1.275-.257.505-.296.631-.557 1.828-.122.549-.266 1.206-.327 1.459-.257 1.106-.305 1.754-.161 2.268.213.762.71 1.11 3.644 2.564 2.277 1.124 2.808 1.463 2.808 1.79 0 .178-.344 1.693-.418 1.833-.135.26-.213.274-1.628.274-1.389 0-1.454-.009-1.506-.226-.022-.075.047-.475.191-1.12.122-.548.213-1.01.2-1.023-.008-.012-1.132.2-2.49.475-1.358.27-2.512.505-2.56.514-.1.017-.157.13-.714 1.38-.052.117-.257.579-.457 1.023s-.492 1.097-.653 1.459c-.161.357-.34.761-.4.892-.061.13-.236.514-.384.849-.152.335-.413.923-.587 1.306-.31.692-1.324 2.956-1.49 3.33-.047.11-.165.358-.256.558-.096.2-.17.37-.17.383 0 .009-.07.165-.157.348-.16.331-.17.453-.021.296.047-.043.444-.553.883-1.127a1251 1251 0 0 1 2.295-2.983c.823-1.066 1.75-2.277 2.068-2.686.409-.544.587-.744.635-.723.214.088.823.244 1.198.31.383.065.831.074 3.722.074 2.93 0 3.335-.01 3.744-.079.744-.122 1.171-.244 1.602-.448.67-.322 1.145-.866 1.476-1.694.135-.348.196-.583.557-2.242.083-.37.187-.853.24-1.066.048-.218.1-.619.117-.893.04-.77-.109-1.219-.535-1.61-.462-.427-.989-.715-4.154-2.256-.97-.474-1.462-.77-1.763-1.075-.235-.235-.24-.275-.082-1.002.26-1.236.317-1.375.622-1.458.087-.022.684-.044 1.323-.044 1.29 0 1.437.022 1.494.244.026.105-.083.68-.335 1.755-.048.2-.079.374-.066.387.022.022.24-.022 2.268-.457 1.746-.374 2.473-.536 2.49-.549.014-.017.249-1.031.41-1.793.109-.527.109-1.145-.004-1.507a1.68 1.68 0 0 0-.601-.835c-.361-.244-1.05-.427-1.903-.51-.696-.065-5.781-.048-6.421.022" })
	});
}
var G = {
	root: "_root_1pxwx_5",
	grid: "_grid_1pxwx_20",
	column: "_column_1pxwx_32",
	columnLastInRow: "_columnLastInRow_1pxwx_42",
	columnLastRow: "_columnLastRow_1pxwx_46",
	columnGrid: "_columnGrid_1pxwx_50",
	columnInner: "_columnInner_1pxwx_54",
	columnInnerCompact: "_columnInnerCompact_1pxwx_70",
	columnInnerGrid: "_columnInnerGrid_1pxwx_74",
	columnInnerBare: "_columnInnerBare_1pxwx_78",
	header: "_header_1pxwx_85",
	headerRight: "_headerRight_1pxwx_97",
	headerCompact: "_headerCompact_1pxwx_108",
	viewport: "_viewport_1pxwx_117",
	viewportVertical: "_viewportVertical_1pxwx_127",
	viewportHorizontal: "_viewportHorizontal_1pxwx_131",
	viewportAbsoluteTop: "_viewportAbsoluteTop_1pxwx_135",
	viewportAbsoluteCenter: "_viewportAbsoluteCenter_1pxwx_140",
	fade: "_fade_1pxwx_147",
	fadeLeft: "_fadeLeft_1pxwx_152",
	fadeRight: "_fadeRight_1pxwx_163",
	fadeTop: "_fadeTop_1pxwx_174",
	fadeBottom: "_fadeBottom_1pxwx_188",
	frame: "_frame_1pxwx_204",
	rail: "_rail_1pxwx_212",
	railStartHorizontal: "_railStartHorizontal_1pxwx_216",
	railStartHorizontalFlipped: "_railStartHorizontalFlipped_1pxwx_222",
	railEndHorizontal: "_railEndHorizontal_1pxwx_228",
	railEndHorizontalFlipped: "_railEndHorizontalFlipped_1pxwx_234",
	railStartVertical: "_railStartVertical_1pxwx_240",
	railEndVertical: "_railEndVertical_1pxwx_246",
	reelViewport: "_reelViewport_1pxwx_254",
	reelClip: "_reelClip_1pxwx_261",
	reelClipHorizontal: "_reelClipHorizontal_1pxwx_266",
	reel: "_reel_1pxwx_254",
	reelVertical: "_reelVertical_1pxwx_281",
	slotAnchor: "_slotAnchor_1pxwx_285",
	slot: "_slot_1pxwx_285",
	slotCard: "_slotCard_1pxwx_299",
	slotImageFrame: "_slotImageFrame_1pxwx_309",
	slotImage: "_slotImage_1pxwx_309",
	slotImageRevealed: "_slotImageRevealed_1pxwx_327",
	slotImageMissing: "_slotImageMissing_1pxwx_332",
	slotValue: "_slotValue_1pxwx_340",
	slotValueCompact: "_slotValueCompact_1pxwx_352",
	slotValueCompactTarget: "_slotValueCompactTarget_1pxwx_357",
	glow: "_glow_1pxwx_362",
	glowSmall: "_glowSmall_1pxwx_371",
	glowVertical: "_glowVertical_1pxwx_375",
	emptyReel: "_emptyReel_1pxwx_381",
	emptyReelVertical: "_emptyReelVertical_1pxwx_391",
	emptyCard: "_emptyCard_1pxwx_395",
	emptyIcon: "_emptyIcon_1pxwx_406",
	vsChip: "_vsChip_1pxwx_412",
	vsChipBox: "_vsChipBox_1pxwx_419",
	starting: "_starting_1pxwx_433",
	startingLabel: "_startingLabel_1pxwx_450",
	startingLuck: "_startingLuck_1pxwx_458",
	startingCount: "_startingCount_1pxwx_462",
	startingDigit: "_startingDigit_1pxwx_471",
	startingGo: "_startingGo_1pxwx_481",
	startingSeats: "_startingSeats_1pxwx_486",
	results: "_results_1pxwx_494",
	resultsSeat: "_resultsSeat_1pxwx_500",
	resultsHeader: "_resultsHeader_1pxwx_506",
	resultsTotal: "_resultsTotal_1pxwx_517",
	resultsCards: "_resultsCards_1pxwx_533",
	resultCard: "_resultCard_1pxwx_543",
	resultCardImage: "_resultCardImage_1pxwx_557",
	resultCardImageMissing: "_resultCardImageMissing_1pxwx_565",
	resultCardName: "_resultCardName_1pxwx_571",
	resultCardValue: "_resultCardValue_1pxwx_584",
	resultPlaceholder: "_resultPlaceholder_1pxwx_591",
	resultPlaceholderLabel: "_resultPlaceholderLabel_1pxwx_600",
	resultPlaceholderNumber: "_resultPlaceholderNumber_1pxwx_606",
	resultsMobile: "_resultsMobile_1pxwx_612",
	resultsMobileSeat: "_resultsMobileSeat_1pxwx_619",
	resultsMobileHeader: "_resultsMobileHeader_1pxwx_626",
	resultsMobileTotal: "_resultsMobileTotal_1pxwx_634",
	resultsMobileStrip: "_resultsMobileStrip_1pxwx_641"
};
//#endregion
//#region src/battle/BattleOpenerDividers.tsx
function K({ layout: e }) {
	return e.playerCount <= 1 || e.dividerMode === "none" ? null : e.dividerMode === "center" ? /* @__PURE__ */ f(q, {
		leftPct: 50,
		topPct: 50
	}) : e.dividerMode === "two-column-rows" ? /* @__PURE__ */ f(d, { children: Array.from({ length: e.rows }, (t, n) => /* @__PURE__ */ f(q, {
		leftPct: 50,
		topPct: (n + .5) / e.rows * 100
	}, n)) }) : /* @__PURE__ */ f(d, { children: Array.from({ length: e.columns - 1 }, (t, n) => /* @__PURE__ */ f(q, {
		leftPct: (n + 1) / e.columns * 100,
		topPct: 50
	}, n)) });
}
function q({ leftPct: e, topPct: t }) {
	return /* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: G.vsChip,
		style: {
			left: `${e}%`,
			top: `${t}%`
		},
		children: /* @__PURE__ */ f("div", {
			className: G.vsChipBox,
			children: /* @__PURE__ */ f(ce, { size: 16 })
		})
	});
}
//#endregion
//#region src/core/dropTable.ts
var J = 1e6;
function le(e, t) {
	let n = e / t;
	return n === Infinity ? 0 : n > .7 && n < 1 ? 1 : n >= 1 && n < 1.5 ? 2 : n >= 1.5 && n < 6.5 ? 3 : n >= 6.5 ? 4 : 0;
}
function ue(e) {
	let t = (e || 0) / J * 100;
	return Number(t.toFixed(4));
}
function de(e, t) {
	let n = 0;
	for (let t = 0; t < e.length; t++) n += e[t]?.weight ?? 0;
	if (n !== 1e6) throw Error(`[loot-os] drop-table weights must sum to ${J}, got ${n}`);
	let r = t;
	for (let t = 0; t < e.length; t++) if (r -= e[t]?.weight ?? 0, r < 0) return t;
}
//#endregion
//#region src/battle/battleSpinnerGeometry.ts
var fe = .68, pe = .25, me = 1, he = 1, ge = 107 / 128, _e = 96 / 128, Y = .5, ve = .3, ye = 68, be = 108, xe = 81, Se = 52, Ce = 44, we = 70, Te = 51, Ee = 37, De = 6, Oe = 22, ke = 79, Ae = 126, je = 42, Me = 54, Ne = 162, Pe = 64, Fe = -95, Ie = 1, Le = 8, Re = 6, ze = 32, Be = 16, Ve = 111, He = 100, Ue = 83, We = 81, Ge = 51, Ke = 24, qe = 65 / 80, Je = 60 / 71, Ye = .25, Xe = 8, Ze = 6;
function Qe(e, t) {
	let n = [], r = 0;
	for (let i of e) n.push(r), r += i + t;
	return n;
}
function $e({ index: e, endingItemIdx: t, isHorizontal: n, itemHeight: r, itemWidth: i }) {
	return n ? i <= 60 ? e === t ? We : Ge : e === t ? i : i <= 100 ? Ue : Math.abs(e - t) === 1 ? Ve : He : e === t ? Ne : e === t - 1 ? Me : e === t + 1 ? Pe : r;
}
function et({ isHorizontal: e, itemWidth: t }) {
	return e ? t <= 60 ? Ke : t <= 100 ? Be : ze : je;
}
function tt({ index: e, endingItemIdx: t, isHorizontal: n, itemWidth: r }) {
	return n ? e === t && r <= 60 ? -3 : e === t && r >= 120 ? 6 : 0 : e === t ? -28 : e === t - 1 ? Fe : e === t + 1 ? Ie : 0;
}
function nt({ offset: e, index: t, endingItemIdx: n, isHorizontal: r, itemHeight: i, itemWidth: a, slotMainSize: o }) {
	return r ? e + o / 2 : e + tt({
		index: t,
		endingItemIdx: n,
		isHorizontal: r,
		itemWidth: a
	}) + i / 2;
}
function rt(e) {
	return 68 + e / 2;
}
function it(e) {
	return fe + .31999999999999995 * e;
}
function at(e) {
	return pe + .75 * e;
}
function ot({ distance: e, itemWidth: t }) {
	let n = ct(t);
	return lt({
		distance: e,
		nearDistance: n.nearDistance,
		farDistance: n.farDistance,
		centerValue: me,
		nearValue: n.nearScale,
		farValue: n.farScale,
		baseValue: fe
	});
}
function st({ distance: e, itemWidth: t }) {
	let n = ct(t);
	return lt({
		distance: e,
		nearDistance: n.nearDistance,
		farDistance: n.farDistance,
		centerValue: he,
		nearValue: n.nearOpacity,
		farValue: n.farOpacity,
		baseValue: pe
	});
}
function ct(e) {
	let t = e <= 100, n = e <= 60, r = et({
		isHorizontal: !0,
		itemWidth: e
	}), i = n ? Ge : t ? Ue : Ve, a = n ? Ge : t ? Ue : He, o = n ? qe : e === 71 ? Je : ge, s = n ? qe : e === 71 ? Je : t ? ge : _e, c = n ? Ye : Y, l = n ? Ye : t ? Y : ve, u = e / 2 + r + i / 2;
	return {
		nearDistance: u,
		farDistance: u + i / 2 + r + a / 2,
		nearScale: o,
		farScale: s,
		nearOpacity: c,
		farOpacity: l
	};
}
function lt({ distance: e, nearDistance: t, farDistance: n, centerValue: r, nearValue: i, farValue: a, baseValue: o }) {
	return ut(e <= t ? {
		progress: e / t,
		from: r,
		to: i
	} : e <= n ? {
		progress: (e - t) / (n - t),
		from: i,
		to: a
	} : {
		progress: Math.min(1, (e - n) / t),
		from: a,
		to: o
	});
}
function ut({ progress: e, from: t, to: n }) {
	return t + (n - t) * e;
}
function dt({ isHorizontal: e, itemWidth: t }) {
	return e ? t <= 60 ? {
		width: Te,
		height: Ee
	} : t === 71 ? {
		width: Ce,
		height: we
	} : t <= 100 ? {
		width: xe,
		height: Se
	} : {
		width: ye,
		height: be
	} : {
		width: ke,
		height: Ae
	};
}
function ft(e) {
	return e === 71 ? De : e <= 60 ? Oe : 40;
}
function pt({ index: e, endingItemIdx: t, isHorizontal: n, itemWidth: r }) {
	return n ? r === 71 ? e === t ? Xe : Ze : e === t && r <= 60 ? 9 : 4 : e === t ? Le : Re;
}
//#endregion
//#region src/battle/BattleEmptyReel.tsx
var mt = [
	.12,
	.2,
	.36,
	.58,
	.36,
	.2,
	.12
];
function ht({ orientation: e, item: t }) {
	return /* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: W(G.emptyReel, e === "vertical" && G.emptyReelVertical),
		style: { gap: 12 },
		"data-los-battle-empty-reel": !0,
		children: mt.map((e, n) => /* @__PURE__ */ f("div", {
			className: G.emptyCard,
			style: {
				height: t.itemHeight,
				width: t.itemWidth,
				opacity: e
			},
			children: /* @__PURE__ */ f(ce, {
				size: Math.min(52, t.itemWidth - 12),
				className: G.emptyIcon
			})
		}, n))
	});
}
//#endregion
//#region src/spinner/timings.ts
var gt = 300, _t = 2500, vt = 1500, yt = 250, bt = 150, xt = 3, St = 35, Ct = {
	normal: 1500,
	quick: 1e3
};
//#endregion
//#region src/spinner/wheel.ts
function wt(e, t, n) {
	let r = [];
	for (let i = 0; i < t; i++) {
		let t;
		t = n ? v(n, 1, J) : Math.round(Math.random() * J);
		let i = e[de(e, t) ?? 0];
		i && r.push(i);
	}
	return r;
}
function Tt(e, t, n, r) {
	let i = wt(e.dropTable, t + 5, r).map((t) => ({
		...t,
		rarity: le(t.value, e.priceCents ?? 0),
		chance: ue(t.weight ?? 0)
	}));
	return n && i.splice(t, 0, {
		...n,
		rarity: le(n.value, e.priceCents ?? 0),
		chance: i.find((e) => e.itemId === n.itemId)?.chance || ue(n.weight ?? 0)
	}), i;
}
//#endregion
//#region src/battle/BattleSpinnerSlot.tsx
var Et = .45, Dt = t(function({ item: e, isTarget: t, revealed: n, itemHeight: r, itemWidth: a, isHorizontal: o, slotMainSize: s, slotVisualOffset: c, labelGap: l, itemCenter: u, centerProgressDistance: d, containerCenter: v, reelOffset: y, lockDurationSec: b, isAnimating: x }) {
	let { theme: S, currency: C } = B(), w = t && n, T = g(+!!w), D = (e) => Math.max(0, Math.min(1, 1 - e / d)), O = _(y, (e) => {
		let n = Math.abs(e + u - v);
		if (o) return `translate3d(0, ${c}px, 0) scale(${ot({
			distance: n,
			itemWidth: a
		})})`;
		let i = it(D(n));
		return `translate3d(0, ${t ? c + r / 2 * (1 - i) : c}px, 0) scale(${i})`;
	}), k = _([y, T], (e) => {
		let n = e[0] ?? 0, r = e[1] ?? 0, i = Math.abs(n + u - v), s = o ? st({
			distance: i,
			itemWidth: a
		}) : at(D(i)), c = Math.min(s, .65);
		return t ? c + (1 - c) * r : c;
	}), A = _(y, (e) => Math.round(D(Math.abs(e + u - v)) * 10));
	i(() => {
		if (!t) {
			T.set(0);
			return;
		}
		let e = m(T, +!!w, {
			type: "tween",
			duration: Math.min(b, Et),
			ease: "easeOut"
		});
		return () => e.stop();
	}, [
		t,
		T,
		b,
		w
	]);
	let j = a <= 60;
	return /* @__PURE__ */ p(h.div, {
		className: G.slot,
		"data-los-target": t ? "true" : void 0,
		style: {
			width: o ? s : "100%",
			height: o ? r : s,
			zIndex: w ? 15 : o ? void 0 : A,
			willChange: x ? "transform, opacity" : void 0
		},
		children: [t && !o ? /* @__PURE__ */ f(Ot, {
			color: P(S.railHex, e.rarity),
			itemWidth: a,
			lockProgress: T
		}) : null, /* @__PURE__ */ p(h.div, {
			className: G.slotCard,
			style: {
				width: a,
				gap: l,
				transform: O,
				transformOrigin: o ? "center center" : "center top",
				willChange: x ? "transform" : void 0
			},
			children: [/* @__PURE__ */ f(h.div, {
				className: G.slotImageFrame,
				style: {
					width: a,
					height: r,
					opacity: k,
					willChange: w || x ? "opacity" : void 0
				},
				children: e.imageUrl ? /* @__PURE__ */ f("img", {
					"data-los-battle-card": !0,
					src: e.imageUrl,
					alt: e.name,
					loading: "eager",
					decoding: "async",
					width: a,
					height: r,
					className: W(G.slotImage, w && G.slotImageRevealed),
					style: { borderRadius: a < 90 ? 4 : 8 }
				}) : /* @__PURE__ */ f("span", {
					"data-los-battle-card": !0,
					"aria-hidden": !0,
					className: G.slotImageMissing,
					style: { borderRadius: a < 90 ? 4 : 8 }
				})
			}), /* @__PURE__ */ f(h.div, {
				style: { opacity: k },
				children: /* @__PURE__ */ f("span", {
					className: W(G.slotValue, j && (t ? G.slotValueCompactTarget : G.slotValueCompact)),
					children: E(e.value, C, { compact: !0 })
				})
			})]
		})]
	});
});
function Ot({ color: e, itemWidth: t, lockProgress: n }) {
	let r = _(n, (e) => e * .8), i = dt({
		isHorizontal: !1,
		itemWidth: t
	});
	return /* @__PURE__ */ f(h.div, {
		"aria-hidden": !0,
		className: W(G.glow, G.glowVertical),
		style: {
			backgroundColor: e,
			height: i.height,
			width: i.width,
			opacity: r
		}
	});
}
function kt({ slotOffsets: e, slotMainSizes: t, viewportStart: n, viewportEnd: r, bufferPx: i, bufferStartPx: a = i, bufferEndPx: o = i }) {
	let s = Math.min(e.length, t.length);
	if (s <= 0) return null;
	let c = n - a, l = r + o, u = At({
		slotOffsets: e,
		slotMainSizes: t,
		slotCount: s,
		startBoundary: c
	}), d = jt({
		slotOffsets: e,
		slotCount: s,
		endBoundary: l
	});
	if (u < 0 || d < 0 || u > d) {
		let n = e[0] ?? 0, r = s - 1, i = (e[r] ?? 0) + (t[r] ?? 0);
		return l < n ? {
			startIndex: 0,
			endIndex: 0
		} : c > i ? {
			startIndex: r,
			endIndex: r
		} : null;
	}
	return {
		startIndex: u,
		endIndex: d
	};
}
function At({ slotOffsets: e, slotMainSizes: t, slotCount: n, startBoundary: r }) {
	let i = 0, a = n - 1, o = -1;
	for (; i <= a;) {
		let n = Math.floor((i + a) / 2);
		(e[n] ?? 0) + (t[n] ?? 0) < r ? i = n + 1 : (o = n, a = n - 1);
	}
	return o;
}
function jt({ slotOffsets: e, slotCount: t, endBoundary: n }) {
	let r = 0, i = t - 1, a = -1;
	for (; r <= i;) {
		let t = Math.floor((r + i) / 2);
		(e[t] ?? 0) > n ? i = t - 1 : (a = t, r = t + 1);
	}
	return a;
}
function Mt({ visibleRange: e, targetIndex: t, slotCount: n, targetOverscanSlots: r = 1 }) {
	if (n <= 0) return [];
	let i = n - 1, a = Lt(t - r, t + r, i);
	return Rt([e ? Lt(e.startIndex, e.endIndex, i) : a, a]);
}
function Nt({ visibleRange: e, slotCount: t, stepSlots: n }) {
	if (!e || t <= 0 || n <= 1) return e;
	let r = t - 1;
	return Lt(Math.floor(e.startIndex / n) * n, Math.ceil(e.endIndex / n) * n, r);
}
function Pt({ targetIndex: e, slotCount: t, overscanSlots: n = 6 }) {
	return t <= 0 ? null : Lt(3 - n, e + n, t - 1);
}
function Ft(e) {
	let t = [], n = /* @__PURE__ */ new Set();
	for (let r of e) for (let e = r.startIndex; e <= r.endIndex; e += 1) n.has(e) || (n.add(e), t.push(e));
	return t;
}
function It(e, t) {
	let n = e[0];
	if (n === void 0) return -1;
	if (t <= n) return 0;
	let r = e.length - 1, i = e[r];
	if (i !== void 0 && t >= i) return r;
	let a = 0, o = r;
	for (; a < o;) {
		let n = Math.floor((a + o) / 2);
		(e[n] ?? Infinity) < t ? a = n + 1 : o = n;
	}
	let s = a, c = Math.max(0, s - 1), l = e[c] ?? 0, u = e[s] ?? l;
	return t - l <= u - t ? c : s;
}
function Lt(e, t, n) {
	return {
		startIndex: Math.max(0, Math.min(e, n)),
		endIndex: Math.max(0, Math.min(t, n))
	};
}
function Rt(e) {
	let t = e.filter((e) => e.startIndex <= e.endIndex).sort((e, t) => e.startIndex - t.startIndex), n = [];
	for (let e of t) {
		let t = n[n.length - 1];
		if (!t || e.startIndex > t.endIndex + 1) {
			n.push({ ...e });
			continue;
		}
		t.endIndex = Math.max(t.endIndex, e.endIndex);
	}
	return n;
}
//#endregion
//#region src/battle/useBattleSpinnerMeasurement.ts
function zt(e, t, n) {
	let [r, i] = l(n);
	return o(() => {
		let n = e.current;
		if (!n) return;
		let r = () => {
			let e = n.getBoundingClientRect(), r = t === "horizontal" ? e.width : e.height;
			r > 0 && i(r);
		};
		if (r(), typeof ResizeObserver > "u") return;
		let a = new ResizeObserver(r);
		return a.observe(n), () => a.disconnect();
	}, [e, t]), r || n;
}
//#endregion
//#region src/battle/BattleSpinner.tsx
var Bt = [
	.25,
	.8,
	.5,
	1
], Vt = 160;
function Ht({ box: e, revealedItem: t, prandGenerator: r, running: a, onRevealComplete: o, laneIndex: u, invertSound: d, orientation: v, containerSizeFallback: y, item: b, revealed: x, spinDelayMs: S, spinMs: C, showMs: w, spinItems: T, staggerMs: E, renderBufferSlots: D, renderBufferEndSlots: O, renderWindowStepSlots: k }) {
	let { sounds: A, theme: j } = B(), M = v === "horizontal", ee = c(null), N = c(null), te = zt(ee, v, y), { itemHeight: F, itemWidth: I } = b, L = 3 + T, ne = (C + u * E) / 1e3, re = w / 1e3, [R] = l(() => e.dropTable.length ? Tt(e, L, t, r) : null), z = R?.length ?? L + 2, ie = et({
		isHorizontal: M,
		itemWidth: I
	}), V = s(() => Array.from({ length: z }, (e, t) => $e({
		index: t,
		endingItemIdx: L,
		isHorizontal: M,
		itemHeight: F,
		itemWidth: I
	})), [
		z,
		L,
		M,
		F,
		I
	]), H = s(() => Qe(V, ie), [V, ie]), ae = s(() => H.map((e, t) => nt({
		offset: e,
		index: t,
		endingItemIdx: L,
		isHorizontal: M,
		itemHeight: F,
		itemWidth: I,
		slotMainSize: V[t] ?? F
	})), [
		H,
		L,
		M,
		F,
		I,
		V
	]), oe = M ? te / 2 : 68 + F / 2, U = oe - (ae[L] ?? 0), se = oe - (ae[3] ?? H[3] ?? 0), ce = M ? I + ie : rt(F), K = s(() => {
		let e = z - 1;
		return (H[e] ?? 0) + (V[e] ?? 0);
	}, [
		z,
		H,
		V
	]), q = (s(() => V.reduce((e, t) => Math.max(e, t), 0), [V]) || (M ? I : F)) + ie, J = q * D, le = q * O, ue = n((e) => Nt({
		visibleRange: kt({
			slotOffsets: H,
			slotMainSizes: V,
			viewportStart: -e,
			viewportEnd: te - e,
			bufferPx: J,
			bufferEndPx: le
		}),
		slotCount: z,
		stepSlots: k
	}), [
		te,
		le,
		J,
		k,
		H,
		V,
		z
	]), [de, fe] = l(() => ue(x ? U : se)), pe = c(de), me = n((e) => {
		let t = ue(e), n = pe.current;
		(n?.startIndex !== t?.startIndex || n?.endIndex !== t?.endIndex) && (pe.current = t, fe(t));
	}, [ue]), he = s(() => R ? Ft(Mt({
		visibleRange: de,
		targetIndex: L,
		slotCount: z
	})).flatMap((e) => {
		let t = R[e];
		return t ? [{
			card: t,
			index: e
		}] : [];
	}) : [], [
		R,
		de,
		L,
		z
	]);
	i(() => {
		if (!R || typeof Image > "u") return;
		let e = Pt({
			targetIndex: L,
			slotCount: z
		});
		if (!e) return;
		let t = /* @__PURE__ */ new Set();
		for (let n = e.startIndex; n <= e.endIndex; n += 1) {
			let e = R[n]?.imageUrl;
			!e || t.has(e) || (t.add(e), new Image().src = e);
		}
	}, [
		R,
		L,
		z
	]);
	let [ge, _e] = l(!1);
	i(() => {
		if (!R || ge) return;
		let e = !1, t = () => {
			e || _e(!0);
		};
		return Gt().then(() => {
			if (e) return;
			let n = N.current;
			if (!n) return t();
			let r = Array.from(n.querySelectorAll("img[data-los-battle-card]"));
			Promise.race([Promise.all(r.map(Wt)).catch(() => void 0), Kt(Vt)]).then(Gt).then(Gt).then(t);
		}), () => {
			e = !0;
		};
	}, [R, ge]);
	let [Y, ve] = l("idle"), ye = c(!1), be = c(null), xe = c(null);
	i(() => {
		a && (ye.current = !1, be.current = performance.now(), xe.current &&= (clearTimeout(xe.current), null), ve("spinning"));
	}, [a]), i(() => () => {
		xe.current && clearTimeout(xe.current);
	}, []);
	let Se = g(se), Ce = _(Se, (e) => M ? `translate3d(${e}px, 0, 0)` : `translate3d(0, ${e}px, 0)`), we = c(0), Te = c(null), Ee = n((e) => {
		if (!R) return;
		let t = Date.now();
		if (t - we.current < 16) return;
		let n = It(ae, oe - e);
		n === Te.current || n < 0 || n >= R.length || (Te.current = n, we.current = t, A.playTickSound());
	}, [
		R,
		ae,
		oe,
		A
	]), De = () => {
		if (ye.current) return;
		ye.current = !0;
		let e = R?.[L]?.rarity ?? 0;
		A.playRaritySound(d ? Ut(e) : e), o(e);
	}, Oe = c(De);
	Oe.current = De, i(() => {
		if (Y !== "spinning") {
			Se.set(se), me(se);
			return;
		}
		let e = S + C + u * E - 50, t = m(Se.get(), U, {
			type: "tween",
			duration: ne,
			delay: S / 1e3,
			ease: Bt,
			onUpdate: (e) => {
				Se.set(e), me(e), u === 0 && Ee(e);
			},
			onComplete: () => {
				if (ye.current) return;
				let t = be.current, n = t === null ? e : performance.now() - t;
				if (n < e) {
					xe.current = setTimeout(() => Oe.current(), e - n);
					return;
				}
				Oe.current();
			}
		});
		return () => t.stop();
	}, [
		Y,
		Se,
		se,
		U,
		ne,
		S,
		C,
		E,
		u,
		Ee,
		me
	]), i(() => {
		x && me(U);
	}, [
		x,
		U,
		me
	]);
	let ke = g(+!!x), Ae = _(ke, (e) => e * .8);
	if (i(() => {
		let e = m(ke, +!!x, {
			type: "tween",
			duration: Math.min(re, .45),
			ease: "easeOut"
		});
		return () => e.stop();
	}, [
		ke,
		re,
		x
	]), !R) return null;
	let je = R[L], Me = dt({
		isHorizontal: !0,
		itemWidth: I
	});
	return /* @__PURE__ */ p("div", {
		ref: ee,
		className: G.reelViewport,
		children: [
			M && x && je ? /* @__PURE__ */ f(h.div, {
				"aria-hidden": !0,
				className: W(G.glow, I <= 80 && G.glowSmall),
				style: {
					backgroundColor: P(j.railHex, je.rarity),
					height: Me.height,
					width: Me.width,
					top: ft(I),
					opacity: Ae
				}
			}) : null,
			ge ? null : /* @__PURE__ */ f(ht, {
				orientation: v,
				item: b
			}),
			/* @__PURE__ */ f("div", {
				className: W(G.reelClip, M && G.reelClipHorizontal),
				children: /* @__PURE__ */ f(h.div, {
					ref: N,
					"data-los-battle-reel": !0,
					"data-los-lane": u,
					className: W(G.reel, !M && G.reelVertical),
					style: {
						...M ? {
							height: F,
							width: K
						} : { height: K },
						transform: Ce,
						willChange: Y === "spinning" ? "transform" : void 0,
						opacity: ge ? 1 : .001
					},
					children: he.map(({ card: e, index: t }) => /* @__PURE__ */ f("div", {
						"data-los-slot-index": t,
						className: G.slotAnchor,
						style: M ? {
							left: H[t] ?? 0,
							top: 0
						} : {
							left: 0,
							top: H[t] ?? 0,
							width: "100%"
						},
						children: /* @__PURE__ */ f(Dt, {
							item: e,
							isTarget: t === L,
							revealed: t === L && x,
							itemHeight: F,
							itemWidth: I,
							isHorizontal: M,
							slotMainSize: V[t] ?? F,
							slotVisualOffset: tt({
								index: t,
								endingItemIdx: L,
								isHorizontal: M,
								itemWidth: I
							}),
							labelGap: pt({
								index: t,
								endingItemIdx: L,
								isHorizontal: M,
								itemWidth: I
							}),
							itemCenter: ae[t] ?? 0,
							centerProgressDistance: ce,
							containerCenter: oe,
							reelOffset: Se,
							lockDurationSec: re,
							isAnimating: Y === "spinning" || x
						})
					}, `${t}-${e.itemId}`))
				})
			})
		]
	});
}
function Ut(e) {
	return e >= 4 ? 0 : e <= 1 ? 4 : e;
}
function Wt(e) {
	return e.complete && e.naturalWidth > 0 ? Promise.resolve() : typeof e.decode == "function" ? e.decode().catch(() => void 0) : new Promise((t) => {
		let n = () => {
			e.removeEventListener("load", n), e.removeEventListener("error", n), t();
		};
		e.addEventListener("load", n, { once: !0 }), e.addEventListener("error", n, { once: !0 });
	});
}
function Gt() {
	return new Promise((e) => {
		if (typeof requestAnimationFrame > "u") {
			setTimeout(e, 0);
			return;
		}
		requestAnimationFrame(() => e());
	});
}
function Kt(e) {
	return new Promise((t) => {
		setTimeout(t, e);
	});
}
//#endregion
//#region src/battle/BattleSpinnerChrome.tsx
var qt = {
	default: {
		left: {
			top: 25,
			bottom: 25,
			width: 18
		},
		right: {
			top: 25,
			bottom: 25,
			width: 18
		}
	},
	"desktop-compact": {
		left: {
			top: 0,
			bottom: 0,
			width: 37
		},
		right: {
			top: 0,
			bottom: 0,
			width: 37
		}
	},
	"mobile-2p": {
		left: {
			top: 0,
			height: 190,
			width: 37
		},
		right: {
			top: 0,
			height: 190,
			width: 37
		}
	},
	"mobile-3p": {
		left: {
			top: 5,
			height: 95,
			width: 12
		},
		right: {
			top: 0,
			height: 100,
			width: 12
		}
	},
	"mobile-grid": {
		left: {
			top: 0,
			height: 137,
			width: 12
		},
		right: {
			top: 0,
			height: 137,
			width: 12
		}
	}
};
function Jt({ orientation: e, variant: t }) {
	if (e === "vertical") return /* @__PURE__ */ p(d, { children: [/* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: W(G.fade, G.fadeTop)
	}), /* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: W(G.fade, G.fadeBottom)
	})] });
	let n = qt[t];
	return /* @__PURE__ */ p(d, { children: [/* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: W(G.fade, G.fadeLeft),
		style: n.left
	}), /* @__PURE__ */ f("div", {
		"aria-hidden": !0,
		className: W(G.fade, G.fadeRight),
		style: n.right
	})] });
}
//#endregion
//#region src/battle/BattleRarityFrame.tsx
function Yt({ orientation: e, variant: t, color: n, inset: r = 0, endInset: i, side: a = "both", className: o }) {
	let s = i ?? r;
	if (e === "vertical") return /* @__PURE__ */ p("div", {
		"aria-hidden": !0,
		className: W(G.frame, o),
		children: [a === "end" ? null : /* @__PURE__ */ f(Qt, {
			className: W(G.rail, G.railStartVertical),
			color: n,
			style: { left: r }
		}), a === "start" ? null : /* @__PURE__ */ f(Qt, {
			className: W(G.rail, G.railEndVertical),
			color: n,
			style: { right: s }
		})]
	});
	let c = t === "desktop" ? Zt : Xt, l = t === "desktop";
	return /* @__PURE__ */ p("div", {
		"aria-hidden": !0,
		className: W(G.frame, o),
		children: [a === "end" ? null : /* @__PURE__ */ f(c, {
			className: W(G.rail, l ? G.railStartHorizontalFlipped : G.railStartHorizontal),
			color: n,
			style: { top: r }
		}), a === "start" ? null : /* @__PURE__ */ f(c, {
			className: W(G.rail, l ? G.railEndHorizontal : G.railEndHorizontalFlipped),
			color: n,
			style: { bottom: s }
		})]
	});
}
function Xt({ className: e, color: t, style: n }) {
	let r = en();
	return /* @__PURE__ */ p("svg", {
		"aria-hidden": "true",
		className: e,
		width: "62",
		height: "9",
		viewBox: "0 0 62 9",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		style: n,
		children: [/* @__PURE__ */ f("path", {
			d: "M62 0.611684L36.1139 0.501953L31.0527 7.50195L25.929 0.503092M0 0.611395L12.6531 0.611684L25.929 0.503092L31.0527 4.75195L36.1139 0.503092",
			stroke: `url(#${r})`
		}), /* @__PURE__ */ f($t, {
			id: r,
			color: t,
			length: "62"
		})]
	});
}
function Zt({ className: e, color: t, style: n }) {
	let r = en();
	return /* @__PURE__ */ p("svg", {
		"aria-hidden": "true",
		className: e,
		width: "74",
		height: "14",
		viewBox: "0 0 74 14",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		style: n,
		children: [/* @__PURE__ */ f("path", {
			d: "M0.00390625 13.3119L30.6914 13.5L36.6914 1.49988L42.7655 13.498M73.5039 13.3124L58.5039 13.3119L42.7655 13.498L36.6914 4.49988L30.6914 13.498",
			stroke: `url(#${r})`
		}), /* @__PURE__ */ f($t, {
			id: r,
			color: t,
			length: "74"
		})]
	});
}
function Qt({ className: e, color: t, style: n }) {
	let r = en();
	return /* @__PURE__ */ p("svg", {
		"aria-hidden": "true",
		className: e,
		width: "14",
		height: "74",
		viewBox: "0 0 14 74",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		style: n,
		children: [/* @__PURE__ */ f("path", {
			d: "M0.688111 0.00390625L0.5 30.6914L12.5001 36.6914L0.501953 42.7655M0.687616 73.5039L0.688111 58.5039L0.501953 42.7655L9.50012 36.6914L0.501953 30.6914",
			stroke: `url(#${r})`
		}), /* @__PURE__ */ f("defs", { children: /* @__PURE__ */ p("linearGradient", {
			id: r,
			x1: "6.75",
			y1: "0",
			x2: "6.75",
			y2: "74",
			gradientUnits: "userSpaceOnUse",
			children: [
				/* @__PURE__ */ f("stop", {
					stopColor: t,
					stopOpacity: "0"
				}),
				/* @__PURE__ */ f("stop", {
					offset: "0.5",
					stopColor: t
				}),
				/* @__PURE__ */ f("stop", {
					offset: "1",
					stopColor: t,
					stopOpacity: "0"
				})
			]
		}) })]
	});
}
function $t({ id: e, color: t, length: n }) {
	return /* @__PURE__ */ f("defs", { children: /* @__PURE__ */ p("linearGradient", {
		id: e,
		x1: n,
		y1: "4.15",
		x2: "0",
		y2: "4.15",
		gradientUnits: "userSpaceOnUse",
		children: [
			/* @__PURE__ */ f("stop", {
				stopColor: t,
				stopOpacity: "0"
			}),
			/* @__PURE__ */ f("stop", {
				offset: "0.5",
				stopColor: t
			}),
			/* @__PURE__ */ f("stop", {
				offset: "1",
				stopColor: t,
				stopOpacity: "0"
			})
		]
	}) });
}
function en() {
	return `los-battle-frame-${a().replace(/:/g, "")}`;
}
//#endregion
//#region src/battle/BattleSpinnerPointers.tsx
function tn({ orientation: e, color: t, compact: n, config: r }) {
	return e === "horizontal" ? n ? r.compactEdgePointers ? /* @__PURE__ */ f(Yt, {
		orientation: "horizontal",
		variant: "mobile",
		color: t,
		inset: r.compactEdgeInset ?? 42,
		endInset: r.compactEdgeEndInset ?? 6
	}) : /* @__PURE__ */ f(Yt, {
		orientation: "horizontal",
		variant: "mobile",
		color: t,
		inset: 12
	}) : /* @__PURE__ */ f(Yt, {
		orientation: "horizontal",
		variant: "desktop",
		color: t,
		inset: r.horizontalInset ?? 56,
		endInset: r.horizontalEndInset ?? 12
	}) : /* @__PURE__ */ f(Yt, {
		orientation: "vertical",
		variant: "desktop",
		color: t,
		inset: r.verticalInset ?? 12
	});
}
//#endregion
//#region src/battle/BattleSeatColumn.tsx
function nn({ seatIndex: e, layout: t, box: r, revealedItem: i, prandGenerator: a, reelKey: o, running: s, revealed: c, invertSound: l, timings: u, header: d, rowIndex: m, isLastInRow: h, isLastRow: g, onRevealComplete: _ }) {
	let { theme: v } = B(), { spinner: y, columnVariant: b, headerAlign: x } = t, S = y.orientation === "horizontal", C = r !== null && i !== null && r.dropTable.length > 0, w = c && r && i ? P(v.railHex, le(i.value, r.priceCents)) : v.colors.border, T = n((t) => _(e, t), [_, e]), E = b === "mobile-grid", D = b === "mobile-2p" || b === "mobile-3p";
	return /* @__PURE__ */ f("div", {
		className: W(G.column, h && G.columnLastInRow, g && G.columnLastRow, b !== "default" && G.columnGrid),
		style: rn({
			columnVariant: b,
			rowIndex: m,
			isLastRow: g
		}),
		"data-los-battle-seat": e,
		children: /* @__PURE__ */ p("div", {
			className: W(G.columnInner, E && G.columnInnerGrid, D && G.columnInnerCompact, x === "right" && G.columnInnerBare),
			children: [
				/* @__PURE__ */ f("div", {
					className: W(G.header, x === "right" && G.headerRight, x === "compact" && G.headerCompact),
					children: d
				}),
				/* @__PURE__ */ f(tn, {
					orientation: y.orientation,
					color: w,
					compact: x === "compact",
					config: y.pointers
				}),
				/* @__PURE__ */ p("div", {
					className: W(G.viewport, S ? G.viewportHorizontal : G.viewportVertical, y.viewport.position === "absolute-top" && G.viewportAbsoluteTop, y.viewport.position === "absolute-center" && G.viewportAbsoluteCenter),
					style: an(y.viewport),
					children: [/* @__PURE__ */ f(Jt, {
						orientation: y.orientation,
						variant: y.chrome
					}), C ? /* @__PURE__ */ f(Ht, {
						box: r,
						revealedItem: i,
						prandGenerator: a,
						running: s,
						onRevealComplete: T,
						laneIndex: e,
						invertSound: l,
						orientation: y.orientation,
						containerSizeFallback: S ? 190 : y.viewport.height,
						item: y.item,
						revealed: c,
						spinDelayMs: u.spinDelayMs,
						spinMs: u.spinMs,
						showMs: u.showMs,
						spinItems: u.spinItems,
						staggerMs: u.staggerMs,
						renderBufferSlots: y.renderBufferSlots,
						renderBufferEndSlots: y.renderBufferEndSlots,
						renderWindowStepSlots: y.renderWindowStepSlots
					}, o) : /* @__PURE__ */ f(ht, {
						orientation: y.orientation,
						item: y.item
					})]
				})
			]
		})
	});
}
function rn({ columnVariant: e, rowIndex: t, isLastRow: n }) {
	switch (e) {
		case "default": return { padding: 6 };
		case "mobile-2p": return { padding: `${t === 0 ? 6 : 3}px 6px ${n ? 6 : 3}px` };
		case "mobile-3p": return { padding: `${t === 0 ? 5 : 3}px 5px ${n ? 6 : 3}px` };
		case "mobile-grid": return { padding: 0 };
	}
}
function an(e) {
	let t = e.marginX ?? 0, n = {
		width: t < 0 && e.width === "100%" ? `calc(100% + ${-2 * t}px)` : e.width,
		height: e.height,
		maxWidth: t < 0 ? "none" : void 0
	};
	return e.position === "flow" && (n.marginTop = e.marginTop, n.marginLeft = t || void 0, n.marginRight = t || void 0), e.position === "absolute-top" && (n.top = e.top), n;
}
//#endregion
//#region src/battle/battleLayout.ts
var on = 768, sn = {
	itemWidth: 128,
	itemHeight: 176
}, cn = {
	itemWidth: 138,
	itemHeight: 190
}, ln = {
	itemWidth: 81,
	itemHeight: 112
}, un = {
	itemWidth: 94,
	itemHeight: 130
}, dn = {
	itemWidth: 58,
	itemHeight: 80
}, fn = {
	itemWidth: 71,
	itemHeight: 98
}, pn = { verticalInset: 24 }, mn = {
	horizontalInset: 56,
	horizontalEndInset: 12
}, hn = {
	horizontalInset: 12,
	horizontalEndInset: 12
}, gn = {
	compactEdgePointers: !0,
	compactEdgeInset: 42,
	compactEdgeEndInset: 6
}, _n = {
	...gn,
	compactEdgeInset: 6
}, vn = {
	renderBufferSlots: 0,
	renderBufferEndSlots: 2,
	renderWindowStepSlots: 2
}, yn = {
	renderBufferSlots: 2,
	renderBufferEndSlots: 2,
	renderWindowStepSlots: 1
}, bn = {
	width: "100%",
	height: 124,
	position: "absolute-top",
	top: 54
};
function xn(e) {
	return !e || e < 2 ? 2 : e >= 5 ? 6 : e;
}
function Sn(e) {
	let t = xn(e.playerCount), n = `ffa-${t}`;
	return e.viewport === "mobile" ? wn(n, t) : Cn(n, t);
}
function Cn(e, t) {
	let n = {
		key: e,
		viewport: "desktop",
		playerCount: t,
		gap: 0,
		padding: 0
	};
	switch (e) {
		case "ffa-2": return {
			...n,
			columns: 2,
			rows: 1,
			height: 380,
			dividerMode: "adjacent",
			columnVariant: "default",
			headerAlign: "center",
			showHeaderTotal: !1,
			spinner: {
				orientation: "horizontal",
				viewport: {
					width: 552,
					height: 188,
					position: "flow",
					marginTop: 38
				},
				item: sn,
				pointers: mn,
				chrome: "default",
				...vn
			},
			results: { cardsPerRow: 4 }
		};
		case "ffa-3": return {
			...n,
			columns: 3,
			rows: 1,
			height: 440,
			dividerMode: "adjacent",
			columnVariant: "default",
			headerAlign: "center",
			showHeaderTotal: !1,
			spinner: {
				orientation: "vertical",
				viewport: {
					width: 402.67,
					height: 364,
					position: "flow"
				},
				item: cn,
				pointers: pn,
				chrome: "default",
				...vn
			},
			results: { cardsPerRow: 3 }
		};
		case "ffa-4": return {
			...n,
			columns: 4,
			rows: 1,
			height: 440,
			dividerMode: "adjacent",
			columnVariant: "default",
			headerAlign: "center",
			showHeaderTotal: !1,
			spinner: {
				orientation: "vertical",
				viewport: {
					width: 293,
					height: 364,
					position: "flow"
				},
				item: cn,
				pointers: pn,
				chrome: "default",
				...vn
			},
			results: { cardsPerRow: 2 }
		};
		case "ffa-6": return {
			...n,
			columns: 2,
			rows: 3,
			height: 726,
			dividerMode: "two-column-rows",
			columnVariant: "default",
			headerAlign: "right",
			showHeaderTotal: !1,
			spinner: {
				orientation: "horizontal",
				viewport: {
					width: 449,
					height: 137,
					position: "absolute-center"
				},
				item: ln,
				pointers: hn,
				chrome: "desktop-compact",
				...vn
			},
			results: { cardsPerRow: 1 }
		};
	}
}
function wn(e, t) {
	let n = {
		key: e,
		viewport: "mobile",
		playerCount: t,
		headerAlign: "compact",
		showHeaderTotal: !0,
		results: { cardsPerRow: 2 }
	};
	switch (e) {
		case "ffa-2": return {
			...n,
			columns: 1,
			rows: 2,
			height: 439,
			gap: 0,
			padding: 0,
			dividerMode: "none",
			columnVariant: "mobile-2p",
			spinner: {
				orientation: "horizontal",
				viewport: {
					width: "100%",
					height: 152,
					position: "flow",
					marginTop: -5,
					marginX: -7
				},
				item: un,
				pointers: {},
				chrome: "mobile-2p",
				...yn
			}
		};
		case "ffa-3": return {
			...n,
			columns: 1,
			rows: 3,
			height: 440,
			gap: 0,
			padding: 0,
			dividerMode: "none",
			columnVariant: "mobile-3p",
			spinner: {
				orientation: "horizontal",
				viewport: {
					width: 336,
					height: 104,
					position: "flow",
					marginTop: -14
				},
				item: dn,
				pointers: _n,
				chrome: "mobile-3p",
				...yn
			}
		};
		case "ffa-4": return {
			...n,
			columns: 2,
			rows: 2,
			height: 420,
			gap: 6,
			padding: 6,
			dividerMode: "center",
			columnVariant: "mobile-grid",
			spinner: {
				orientation: "horizontal",
				viewport: bn,
				item: fn,
				pointers: gn,
				chrome: "mobile-grid",
				...yn
			}
		};
		case "ffa-6": return {
			...n,
			columns: 2,
			rows: 3,
			height: 610,
			gap: 6,
			padding: 6,
			dividerMode: "two-column-rows",
			columnVariant: "mobile-grid",
			spinner: {
				orientation: "horizontal",
				viewport: bn,
				item: fn,
				pointers: gn,
				chrome: "mobile-grid",
				...yn
			}
		};
	}
}
//#endregion
//#region src/battle/battleTimings.ts
var Tn = 3e3, En = [
	2,
	3,
	4,
	6
], Dn = 250, On = 300, kn = {
	spinMs: _t,
	quickSpinMs: vt,
	staggerMs: 250,
	quickStaggerMs: 150,
	spinLeadMs: 250 + On,
	showMs: 1e3,
	holdMs: Ct.normal,
	quickHoldMs: Ct.quick
};
function An(e) {
	let t = kn, n = e.fastSpin ? t.quickSpinMs : t.spinMs, r = e.fastSpin ? t.quickStaggerMs : t.staggerMs, i = e.fastSpin ? t.quickHoldMs : t.holdMs;
	return t.spinLeadMs + n + r * Math.max(0, e.playerCount - 1) + t.showMs + i;
}
function jn(e) {
	let t = kn, n = e.fastSpin ? t.quickSpinMs : t.spinMs, r = e.fastSpin ? t.quickStaggerMs : t.staggerMs;
	return t.spinLeadMs + n + r * Math.max(0, e.laneCount - 1) + t.showMs;
}
function Mn(e) {
	return e.compact ? e.fastSpin ? 10 : 14 : 24;
}
//#endregion
//#region src/battle/seed.ts
function Nn(e) {
	let t = 2166136261;
	for (let n = 0; n < e.length; n++) t ^= e.charCodeAt(n), t = Math.imul(t, 16777619);
	return t | 0;
}
function Pn(e, t = 0) {
	return y(Nn(e) + t);
}
//#endregion
//#region src/battle/BattleOpener.tsx
function Fn(e) {
	let t = H(), r = e.theme ?? t.theme, a = e.currency ?? t.currency ?? C, u = e.locale ?? t.locale, d = s(() => A(u), [u]), m = s(() => re(r), [r]), h = se({
		sounds: t.sounds,
		soundBaseUrl: t.soundBaseUrl
	}), g = e.sounds ?? h, _ = e.quick ?? !1, v = e.invertSound ?? !1, y = e.laneItems.length, b = c(null), [x, S] = l(null);
	o(() => {
		let e = b.current;
		if (!e) return;
		let t = () => S(e.offsetWidth);
		if (t(), typeof ResizeObserver > "u") return;
		let n = new ResizeObserver(t);
		return n.observe(e), () => n.disconnect();
	}, []);
	let w = e.compact ?? "auto", T = w === "auto" ? x !== null && x < 768 : w, E = s(() => Sn({
		playerCount: y,
		viewport: T ? "mobile" : "desktop"
	}), [y, T]), D = s(() => {
		let e = kn;
		return {
			spinDelayMs: 250,
			spinMs: _ ? e.quickSpinMs : e.spinMs,
			showMs: e.showMs,
			spinItems: Mn({
				fastSpin: _,
				compact: T
			}),
			staggerMs: _ ? e.quickStaggerMs : e.staggerMs
		};
	}, [_, T]), O = e.seed, k = s(() => Array.from({ length: y }, (e, t) => Pn(O, t)), [O, y]), [j, M] = l(() => /* @__PURE__ */ new Set()), ee = e.onLaneSettled, N = n((e, t) => {
		M((t) => {
			if (t.has(e)) return t;
			let n = new Set(t);
			return n.add(e), n;
		}), ee?.(e, t);
	}, [ee]), te = y > 0 && j.size >= y, P = e.onRevealComplete, F = c(P);
	F.current = P;
	let I = c(!1);
	i(() => {
		if (!te || I.current) return;
		let e = setTimeout(() => {
			I.current = !0, F.current?.();
		}, On + D.showMs);
		return () => clearTimeout(e);
	}, [te, D.showMs]);
	let L = s(() => ({
		strings: d,
		theme: m,
		sounds: g,
		currency: a
	}), [
		d,
		m,
		g,
		a
	]), ne = {
		gridTemplateColumns: `repeat(${E.columns}, minmax(0, 1fr))`,
		height: E.height,
		gap: E.gap,
		padding: E.padding
	};
	return /* @__PURE__ */ f(ie.Provider, {
		value: L,
		children: /* @__PURE__ */ p("div", {
			ref: b,
			className: W(G.root, e.className),
			style: {
				...m.cssVars,
				...e.style
			},
			"data-los-battle-opener": !0,
			"data-los-battle-layout": E.key,
			"data-los-battle-viewport": E.viewport,
			children: [x === null && w === "auto" ? null : /* @__PURE__ */ p("div", {
				className: G.grid,
				style: ne,
				children: [e.laneItems.map((t, n) => {
					let r = Math.floor(n / E.columns), i = j.has(n);
					return /* @__PURE__ */ f(nn, {
						seatIndex: n,
						layout: E,
						box: e.box,
						revealedItem: t,
						prandGenerator: k[n] ?? Pn(O, n),
						reelKey: `${O}:${n}`,
						running: e.running,
						revealed: i,
						invertSound: v,
						timings: D,
						header: e.renderSeatHeader?.(n, {
							compact: T,
							settled: i,
							layout: E
						}),
						rowIndex: r,
						isLastInRow: (n + 1) % E.columns === 0 || n === y - 1,
						isLastRow: r === E.rows - 1,
						onRevealComplete: N
					}, n);
				}), /* @__PURE__ */ f(K, { layout: E })]
			}), e.overlay]
		})
	});
}
//#endregion
//#region src/battle/BattleResultsGrid.tsx
function In(e) {
	let t = H(), n = s(() => re(e.theme ?? t.theme), [e.theme, t.theme]), r = e.currency ?? t.currency ?? C, i = s(() => A(e.locale ?? t.locale), [e.locale, t.locale]), a = c(null), [u, d] = l(null);
	o(() => {
		let e = a.current;
		if (!e) return;
		let t = () => d(e.offsetWidth);
		if (t(), typeof ResizeObserver > "u") return;
		let n = new ResizeObserver(t);
		return n.observe(e), () => n.disconnect();
	}, []);
	let m = e.compact ?? "auto", h = m === "auto" ? u !== null && u < 768 : m, g = Sn({
		playerCount: e.lanes.length,
		viewport: h ? "mobile" : "desktop"
	}).results.cardsPerRow, _ = s(() => Array.from({ length: e.roundCount }, (e, t) => t), [e.roundCount]), v = (t, a) => {
		let o = t.items[a] ?? null;
		return o ? /* @__PURE__ */ f(Ln, {
			item: o,
			priceCents: e.roundPriceCents[a] ?? 0,
			theme: n,
			currency: r
		}, a) : /* @__PURE__ */ p("div", {
			className: W(G.resultCard, G.resultPlaceholder),
			title: `${i.battleRound} ${a + 1}`,
			children: [/* @__PURE__ */ f("p", {
				className: G.resultPlaceholderLabel,
				children: i.battleRound.toUpperCase()
			}), /* @__PURE__ */ f("p", {
				className: G.resultPlaceholderNumber,
				children: String(a + 1).padStart(2, "0")
			})]
		}, a);
	};
	return /* @__PURE__ */ f("div", {
		ref: a,
		className: W(G.root, e.className),
		style: {
			...n.cssVars,
			...e.style
		},
		"data-los-battle-results": !0,
		children: u === null && m === "auto" ? null : h ? /* @__PURE__ */ f("div", {
			className: G.resultsMobile,
			children: e.lanes.map((t, n) => /* @__PURE__ */ p("div", {
				className: G.resultsMobileSeat,
				children: [/* @__PURE__ */ p("div", {
					className: G.resultsMobileHeader,
					children: [e.renderSeatHeader(n, { compact: !0 }), /* @__PURE__ */ f("span", {
						className: G.resultsMobileTotal,
						children: t.totalCents > 0 ? E(t.totalCents, r) : "-"
					})]
				}), /* @__PURE__ */ f("div", {
					className: G.resultsMobileStrip,
					children: _.map((e) => v(t, e))
				})]
			}, n))
		}) : /* @__PURE__ */ f("div", {
			className: G.results,
			style: { gridTemplateColumns: `repeat(${e.lanes.length}, minmax(0, 1fr))` },
			children: e.lanes.map((t, n) => /* @__PURE__ */ p("div", {
				className: G.resultsSeat,
				children: [/* @__PURE__ */ p("div", {
					className: G.resultsHeader,
					children: [e.renderSeatHeader(n, { compact: !1 }), /* @__PURE__ */ f("div", {
						className: G.resultsTotal,
						children: t.totalCents > 0 ? E(t.totalCents, r) : "-"
					})]
				}), /* @__PURE__ */ f("div", {
					className: G.resultsCards,
					style: { gridTemplateColumns: `repeat(${g}, minmax(0, 1fr))` },
					children: _.map((e) => v(t, e))
				})]
			}, n))
		})
	});
}
function Ln({ item: e, priceCents: t, theme: n, currency: r }) {
	let i = P(n.railHex, le(e.value, t));
	return /* @__PURE__ */ p(h.div, {
		className: G.resultCard,
		style: { borderColor: i },
		initial: {
			opacity: 0,
			scale: .92
		},
		animate: {
			opacity: 1,
			scale: 1
		},
		transition: {
			type: "tween",
			duration: .35,
			ease: "easeOut"
		},
		title: e.name,
		children: [
			e.imageUrl ? /* @__PURE__ */ f("img", {
				className: G.resultCardImage,
				src: e.imageUrl,
				alt: e.name,
				loading: "eager",
				decoding: "async"
			}) : /* @__PURE__ */ f("span", {
				"aria-hidden": !0,
				className: W(G.resultCardImage, G.resultCardImageMissing)
			}),
			/* @__PURE__ */ f("p", {
				className: G.resultCardName,
				children: e.name
			}),
			/* @__PURE__ */ f("span", {
				className: G.resultCardValue,
				style: { color: i },
				children: E(e.value, r, { compact: !0 })
			})
		]
	});
}
//#endregion
//#region src/battle/BattleStartingOverlay.tsx
var Rn = 240;
function zn({ endsAtMs: e, now: t = Date.now, onFinished: n, children: r, className: a }) {
	let { strings: o } = B(), [s, u] = l(() => Math.max(0, e - t())), d = c(!1);
	i(() => {
		d.current = !1;
		let r = () => {
			let r = Math.max(0, e - t());
			u(r), r <= 0 && !d.current && (d.current = !0, n());
		};
		r();
		let i = setInterval(r, 50);
		return () => clearInterval(i);
	}, [
		e,
		t,
		n
	]);
	let m = Math.ceil(s / 1e3), g = m > 0 ? String(m) : o.battleGo, _ = s <= Rn;
	return /* @__PURE__ */ p(h.div, {
		className: W(G.starting, a),
		"data-los-battle-starting": !0,
		initial: !1,
		animate: {
			filter: _ ? "blur(18px)" : "blur(0px)",
			opacity: +!_,
			scale: _ ? 1.015 : 1
		},
		transition: {
			duration: _ ? Rn / 1e3 : 0,
			ease: "easeInOut"
		},
		children: [
			/* @__PURE__ */ f("span", {
				className: G.startingLabel,
				children: o.battleStartsIn
			}),
			/* @__PURE__ */ f("div", {
				className: G.startingCount,
				"aria-live": "polite",
				children: /* @__PURE__ */ f(h.span, {
					className: W(G.startingDigit, m <= 0 && G.startingGo),
					initial: {
						scale: 1.6,
						opacity: 0
					},
					animate: {
						scale: 1,
						opacity: 1
					},
					transition: {
						type: "tween",
						duration: .25,
						ease: "easeOut"
					},
					children: g
				}, g)
			}),
			/* @__PURE__ */ f("span", {
				className: W(G.startingLabel, G.startingLuck),
				children: o.battleGoodLuck
			}),
			r ? /* @__PURE__ */ f("div", {
				className: G.startingSeats,
				children: r
			}) : null
		]
	});
}
//#endregion
//#region src/core/contract.ts
function Bn(e) {
	switch (e) {
		case "revealing": return "during";
		case "results": return "post";
		default: return "pre";
	}
}
function Vn(e) {
	return e;
}
//#endregion
//#region src/core/machine.ts
var Hn = 700;
function Un(e) {
	let { openBox: t, onBeforeOpen: n, revealStartDelayMs: r = 700, minQuantity: i = 1, maxQuantity: a = 5, onEvent: o } = e, s = (e) => Math.max(i, Math.min(a, Math.floor(e) || i)), c = {
		phase: "idle",
		quantity: s(e.defaultQuantity ?? i),
		isQuickReveal: e.defaultQuickReveal ?? !1,
		entries: [],
		lastResponse: null,
		claimStatus: "none",
		pendingClaim: null,
		claim: null,
		claimError: null,
		sale: null,
		sellError: null,
		error: null,
		openCount: 0
	}, l = /* @__PURE__ */ new Set(), u = null, d = 0, f = !1, p = (e) => o?.(e), m = (e) => {
		c = {
			...c,
			...e
		};
		for (let e of l) e();
	}, h = () => {
		u !== null && (clearTimeout(u), u = null);
	}, g = (e) => {
		c.phase === "revealing" && (m({
			phase: "results",
			openCount: c.openCount + 1
		}), e && p({ type: "skipped" }), p({
			type: "reveal_complete",
			response: c.lastResponse,
			skipped: e
		}));
	};
	return {
		getState: () => c,
		subscribe: (e) => (l.add(e), () => l.delete(e)),
		open: async (i = c.quantity) => {
			if (f || c.phase !== "idle") return;
			let a = s(i), o = ++d;
			if (n) {
				let e;
				try {
					e = await n(a);
				} catch {
					e = !1;
				}
				if (e === !1) {
					p({
						type: "open_vetoed",
						quantity: a
					});
					return;
				}
				if (o !== d || c.phase !== "idle") return;
			}
			m({
				phase: "requesting",
				quantity: a,
				entries: [],
				claimStatus: "none",
				pendingClaim: null,
				claim: null,
				claimError: null,
				sale: null,
				sellError: null,
				error: null
			}), p({
				type: "open_start",
				quantity: a
			});
			let l;
			try {
				l = await t(a);
			} catch (e) {
				if (o !== d) return;
				m({
					phase: "idle",
					error: e
				}), p({
					type: "error",
					error: e
				});
				return;
			}
			if (o !== d) return;
			p({
				type: "opened",
				response: l
			});
			let h = () => {
				u = null;
				let t = l.status !== "claimed" && !!e.claimReveal;
				m({
					phase: "revealing",
					entries: l.entries,
					lastResponse: l,
					claimStatus: l.status === "claimed" ? "claimed" : t ? "unclaimed" : "none",
					pendingClaim: t ? {
						openGroupId: l.openGroupId,
						costCents: l.costCents
					} : null,
					claim: l.claim
				}), p({
					type: "reveal_start",
					response: l
				});
			};
			r > 0 ? u = setTimeout(h, r) : h();
		},
		skip: () => g(!0),
		completeReveal: () => g(!1),
		claim: async () => {
			if (f || !e.claimReveal || c.phase !== "results" || c.claimStatus !== "unclaimed") return;
			let t = c.pendingClaim;
			if (!t) return;
			let n = d;
			m({
				claimStatus: "claiming",
				claimError: null,
				sellError: null
			}), p({
				type: "claim_start",
				openGroupId: t.openGroupId
			});
			let r;
			try {
				r = await e.claimReveal(t);
			} catch (e) {
				if (n !== d) return;
				m({
					claimStatus: "unclaimed",
					claimError: e
				}), p({
					type: "claim_error",
					error: e
				});
				return;
			}
			n === d && (m({
				claimStatus: "claimed",
				pendingClaim: null,
				claim: r
			}), p({
				type: "claimed",
				claim: r
			}));
		},
		sellBack: async () => {
			if (f || !e.sellBackReveal || c.phase !== "results" || c.claimStatus !== "unclaimed") return;
			let t = c.pendingClaim;
			if (!t) return;
			let n = d;
			m({
				claimStatus: "selling",
				claimError: null,
				sellError: null
			}), p({
				type: "sell_back_start",
				openGroupId: t.openGroupId
			});
			let r;
			try {
				r = await e.sellBackReveal(t);
			} catch (e) {
				if (n !== d) return;
				m({
					claimStatus: "unclaimed",
					sellError: e
				}), p({
					type: "sell_back_error",
					error: e
				});
				return;
			}
			n === d && (m({
				claimStatus: "sold",
				pendingClaim: null,
				claim: r,
				sale: r
			}), p({
				type: "sold_back",
				sale: r
			}));
		},
		restore: (e) => {
			f || c.phase !== "idle" || c.pendingClaim || (m({
				phase: "results",
				entries: e.entries,
				claimStatus: "unclaimed",
				pendingClaim: {
					openGroupId: e.openGroupId,
					costCents: e.costCents
				},
				claim: null,
				claimError: null,
				sale: null,
				sellError: null,
				error: null
			}), p({
				type: "restored",
				reveal: e
			}));
		},
		reset: () => {
			c.claimStatus !== "unclaimed" && c.claimStatus !== "claiming" && c.claimStatus !== "selling" && (d++, h(), (c.phase !== "idle" || c.error !== null || c.entries.length !== 0) && (m({
				phase: "idle",
				entries: [],
				claimStatus: "none",
				pendingClaim: null,
				claim: null,
				claimError: null,
				sale: null,
				sellError: null,
				error: null
			}), p({ type: "reset" })));
		},
		setQuantity: (e) => {
			let t = s(e);
			t !== c.quantity && (c.phase === "idle" || c.phase === "results") && m({ quantity: t });
		},
		setQuickReveal: (e) => {
			e !== c.isQuickReveal && m({ isQuickReveal: e });
		},
		dispose: () => {
			f = !0, d++, h(), l.clear();
		},
		isDisposed: () => f
	};
}
//#endregion
//#region src/core/revealExit.ts
function Wn({ revealExit: e, isQuickReveal: t, actions: n }) {
	let r = null, i = () => {
		r &&= (clearTimeout(r), null);
	};
	return {
		completeReveal: () => {
			i();
			let a = t ? e.lingerMs.quick : e.lingerMs.normal;
			if (a === 0) {
				n.finishReveal();
				return;
			}
			r = setTimeout(() => {
				r = null, n.finishReveal();
			}, a);
		},
		skipReveal: (e) => {
			e?.(), i(), n.skipReveal();
		},
		cancel: i
	};
}
//#endregion
//#region src/core/types.ts
function Gn(e) {
	return e.demo === !0;
}
function Kn(e) {
	if (typeof e != "object" || !e) return !1;
	let t = e;
	return typeof t.code == "string" && typeof t.message == "string";
}
//#endregion
//#region src/react/lootQueryClient.ts
var qn;
function Jn() {
	return qn ??= new b({ defaultOptions: {
		queries: {
			retry: !1,
			refetchOnWindowFocus: !1,
			refetchOnReconnect: !1,
			staleTime: 3e4
		},
		mutations: { retry: !1 }
	} }), qn;
}
var Yn = /* @__PURE__ */ new WeakMap(), Xn = 0;
function Zn(e) {
	if (!e) return null;
	let t = Yn.get(e);
	return t === void 0 && (Xn += 1, t = Xn, Yn.set(e, t)), t;
}
function Qn() {
	return typeof crypto < "u" && typeof crypto.randomUUID == "function" ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
}
var X = {
	panel: "_panel_1nqtp_1",
	toggle: "_toggle_1nqtp_11",
	chevron: "_chevron_1nqtp_30",
	chevronOpen: "_chevronOpen_1nqtp_35",
	body: "_body_1nqtp_39",
	row: "_row_1nqtp_46",
	label: "_label_1nqtp_52",
	value: "_value_1nqtp_57",
	seedInput: "_seedInput_1nqtp_63",
	entries: "_entries_1nqtp_78",
	entryRow: "_entryRow_1nqtp_87",
	muted: "_muted_1nqtp_94"
}, $n = "los:clientSeed", er = 36;
function tr() {
	return typeof crypto < "u" && typeof crypto.randomUUID == "function" ? crypto.randomUUID().split("-")[0] ?? "seed" : Math.random().toString(36).slice(2, 10);
}
function nr(e) {
	return e.slice(0, 36);
}
function rr() {
	if (typeof window > "u") return null;
	try {
		let e = window.localStorage.getItem($n);
		return e ? nr(e) : null;
	} catch {
		return null;
	}
}
function ir({ clientSeed: e, onClientSeedChange: t }) {
	let r = e !== void 0, [a, o] = l(() => rr() ?? tr());
	i(() => {
		if (!(r || typeof window > "u")) try {
			window.localStorage.setItem($n, a);
		} catch {}
	}, [r, a]);
	let s = n((e) => {
		let n = nr(e);
		t?.(n), r || o(n);
	}, [r, t]);
	return [r ? nr(e) : a, s];
}
//#endregion
//#region src/fairness/ProvablyFairPanel.tsx
function ar() {
	return (/* @__PURE__ */ new Date()).toISOString().slice(0, 10);
}
function or({ client: e, lastResponse: t, clientSeed: n, onClientSeedChange: r, locked: i = !1, defaultOpen: o = !1, className: s }) {
	let { strings: c } = B(), [u, m] = l(o), h = a(), g = t?.fairness ?? null, _ = S({
		queryKey: [
			"loot-os",
			"seed",
			Zn(e),
			ar()
		],
		queryFn: async () => await e?.getSeed?.(ar()) ?? null,
		enabled: u && !g && !!e?.getSeed,
		staleTime: Infinity
	}, Jn()), v = g?.serverSeedHash ?? _.data?.seedHash ?? null;
	return /* @__PURE__ */ p("div", {
		className: W(X.panel, s),
		children: [/* @__PURE__ */ p("button", {
			type: "button",
			className: X.toggle,
			"aria-expanded": u,
			"aria-controls": h,
			onClick: () => m((e) => !e),
			children: [
				/* @__PURE__ */ f("span", {
					"aria-hidden": !0,
					children: "ⓘ"
				}),
				c.provablyFair,
				/* @__PURE__ */ f("span", {
					"aria-hidden": !0,
					className: W(X.chevron, u && X.chevronOpen),
					children: "▾"
				})
			]
		}), u && /* @__PURE__ */ p("div", {
			className: X.body,
			id: h,
			children: [
				/* @__PURE__ */ p("div", {
					className: X.row,
					children: [/* @__PURE__ */ f("span", {
						className: X.label,
						children: c.serverSeedHash
					}), /* @__PURE__ */ f("span", {
						className: X.value,
						children: v ?? "…"
					})]
				}),
				/* @__PURE__ */ p("div", {
					className: X.row,
					children: [/* @__PURE__ */ f("label", {
						className: X.label,
						htmlFor: `${h}-seed`,
						children: c.clientSeed
					}), /* @__PURE__ */ f("input", {
						id: `${h}-seed`,
						className: X.seedInput,
						value: n,
						maxLength: 36,
						disabled: i,
						onChange: (e) => r(e.target.value)
					})]
				}),
				g ? /* @__PURE__ */ p(d, { children: [
					/* @__PURE__ */ p("div", {
						className: X.row,
						children: [/* @__PURE__ */ f("span", {
							className: X.label,
							children: c.salt
						}), /* @__PURE__ */ f("span", {
							className: X.value,
							children: g.salt
						})]
					}),
					/* @__PURE__ */ p("div", {
						className: X.row,
						children: [/* @__PURE__ */ f("span", {
							className: X.label,
							children: c.seedDate
						}), /* @__PURE__ */ f("span", {
							className: X.value,
							children: g.seedDate
						})]
					}),
					/* @__PURE__ */ p("div", {
						className: X.row,
						children: [/* @__PURE__ */ p("span", {
							className: X.label,
							children: [
								c.nonce,
								" / ",
								c.result
							]
						}), /* @__PURE__ */ f("ul", {
							className: X.entries,
							children: (t?.entries ?? []).map((e) => /* @__PURE__ */ p("li", {
								className: X.entryRow,
								children: [/* @__PURE__ */ p("span", { children: ["#", e.nonce] }), /* @__PURE__ */ f("span", { children: e.result })]
							}, e.openId))
						})]
					})
				] }) : /* @__PURE__ */ f("span", {
					className: X.muted,
					children: c.noOpensYet
				})
			]
		})]
	});
}
//#endregion
//#region src/demo/demoOpen.ts
function sr() {
	if (typeof crypto < "u" && typeof crypto.getRandomValues == "function") {
		let e = /* @__PURE__ */ new Uint32Array(1), t = Math.floor(4294967296 / J) * J, n = 0;
		do
			crypto.getRandomValues(e), n = e[0] ?? 0;
		while (n >= t);
		return n % J;
	}
	return Math.floor(Math.random() * J);
}
function cr(e) {
	return typeof crypto < "u" && typeof crypto.randomUUID == "function" ? `${e}_${crypto.randomUUID()}` : `${e}_${Math.random().toString(36).slice(2)}`;
}
function lr(e, t, n) {
	let r = [];
	for (let n = 0; n < t; n++) {
		let t = sr(), i = de(e.dropTable, t) ?? 0, a = e.dropTable[i];
		a && r.push({
			openId: cr("demo_open"),
			nonce: n,
			result: t,
			itemIndex: i,
			item: a
		});
	}
	return {
		demo: !0,
		openGroupId: cr("demo_group"),
		boxId: e.id,
		boxVersion: e.version,
		costCents: e.priceCents * t,
		dropTableHash: e.dropTableHash,
		status: "unclaimed",
		claim: null,
		entries: r,
		fairness: {
			seedDate: (/* @__PURE__ */ new Date()).toISOString().slice(0, 10),
			serverSeedHash: "demo",
			clientSeed: n,
			salt: "demo",
			nonces: r.map((e) => e.nonce)
		}
	};
}
function ur(e, t) {
	return {
		openGroupId: e.openGroupId,
		costCents: e.costCents,
		balanceAfterCents: 0,
		claimedAt: (/* @__PURE__ */ new Date()).toISOString(),
		items: t.map((e) => ({
			openId: e.openId,
			inventoryItemId: cr("demo_inv"),
			item: e.item,
			isLimited: e.item.isLimited ?? !1
		}))
	};
}
function dr(e, t) {
	return {
		soldCents: e.item.value,
		balanceAfterCents: t + e.item.value,
		soldAt: (/* @__PURE__ */ new Date()).toISOString(),
		items: [{
			inventoryItemId: e.inventoryItemId,
			item: e.item
		}]
	};
}
function fr(e, t) {
	let n = ur(e, t), r = t.reduce((e, t) => e + t.item.value, 0);
	return {
		...n,
		balanceAfterCents: n.balanceAfterCents + r,
		soldCents: r,
		soldAt: (/* @__PURE__ */ new Date()).toISOString()
	};
}
//#endregion
//#region src/react/hooks.ts
function pr({ client: e, boxId: t }) {
	let r = S({
		queryKey: [
			"loot-os",
			"box",
			Zn(e),
			t
		],
		queryFn: () => {
			if (!e) throw Error("[loot-os] no API client configured");
			return e.getBox(t);
		},
		enabled: !!e
	}, Jn()), { refetch: i } = r, a = n(() => void i(), [i]);
	return {
		box: r.data ?? null,
		loading: !!e && r.isFetching,
		error: r.isFetching ? null : r.error ?? null,
		reload: a
	};
}
function mr({ client: e, boxId: t, clientSeed: r, expectedVersion: i }) {
	let a = x({
		mutationKey: [
			"loot-os",
			"open",
			Zn(e),
			t
		],
		mutationFn: (n) => {
			if (!e) throw Error("[loot-os] no API client configured");
			return e.openBox({
				boxId: t,
				clientSeed: r,
				amount: n.quantity,
				expectedVersion: i,
				idempotencyKey: n.idempotencyKey
			});
		}
	}, Jn()), { mutateAsync: o } = a;
	return {
		open: n(async (t = 1) => {
			if (!e) return null;
			try {
				return await o({
					quantity: t,
					idempotencyKey: Qn()
				});
			} catch {
				return null;
			}
		}, [e, o]),
		opening: a.isPending,
		response: a.data ?? null,
		error: a.error ?? null
	};
}
//#endregion
//#region src/react/useOpenerMachine.ts
function hr(e) {
	return u(e.subscribe, e.getState, e.getState);
}
function gr(e) {
	let t = c(e);
	t.current = e;
	let n = c(null);
	n.current === null && (n.current = () => Un({
		openBox: (e) => t.current.openBox(e),
		claimReveal: e.claimReveal ? (e) => {
			let n = t.current.claimReveal;
			if (!n) throw Error("[loot-os] claimReveal config removed mid-flight");
			return n(e);
		} : void 0,
		sellBackReveal: e.sellBackReveal ? (e) => {
			let n = t.current.sellBackReveal;
			if (!n) throw Error("[loot-os] sellBackReveal config removed mid-flight");
			return n(e);
		} : void 0,
		onBeforeOpen: (e) => t.current.onBeforeOpen?.(e),
		onEvent: (e) => t.current.onEvent?.(e),
		revealStartDelayMs: e.revealStartDelayMs,
		minQuantity: e.minQuantity,
		maxQuantity: e.maxQuantity,
		defaultQuantity: e.defaultQuantity,
		defaultQuickReveal: e.defaultQuickReveal
	}));
	let r = n.current, [a, o] = l(r);
	return i(() => {
		if (a.isDisposed()) {
			o(r());
			return;
		}
		return () => a.dispose();
	}, [a, r]), {
		machine: a,
		state: hr(a)
	};
}
//#endregion
//#region src/react/useResolvedClient.ts
var _r = async (e) => {
	let { LootClient: t } = await import("@loot-os/client");
	return new t(e);
};
function vr({ client: e, createClient: t, publishableKey: n, baseUrl: r, sessionToken: a, onSessionExpired: o }) {
	let [s, u] = l(null), [d, f] = l(null), p = c(o);
	p.current = o, i(() => {
		if (e || !n) return;
		let i = !1, a = t ?? _r;
		return Promise.resolve(a({
			publishableKey: n,
			baseUrl: r,
			sessionToken: null,
			onSessionExpired: () => p.current ? p.current() : Promise.resolve(null)
		})).then((e) => {
			i || u(e);
		}).catch((e) => {
			i || f(e);
		}), () => {
			i = !0;
		};
	}, [
		e,
		t,
		n,
		r
	]);
	let m = e ?? s;
	return i(() => {
		m?.setSessionToken?.(a ?? null);
	}, [m, a]), {
		client: m,
		error: d
	};
}
var yr = {
	scroller: "_scroller_ujyfz_3",
	track: "_track_ujyfz_13",
	trackAnimated: "_trackAnimated_ujyfz_19",
	losIdleCardScroll: "_losIdleCardScroll_ujyfz_1",
	loop: "_loop_ujyfz_24",
	slot: "_slot_ujyfz_31",
	floatY: "_floatY_ujyfz_37",
	losFloatY: "_losFloatY_ujyfz_1",
	floatYDelay1: "_floatYDelay1_ujyfz_41",
	floatYSmall: "_floatYSmall_ujyfz_45",
	losFloatYSmall: "_losFloatYSmall_ujyfz_1",
	floatYSmallDelay1: "_floatYSmallDelay1_ujyfz_49",
	fadeLeft: "_fadeLeft_ujyfz_53",
	fadeRight: "_fadeRight_ujyfz_70"
}, br = 12;
function xr(e, t) {
	let n = e % 2 == 1;
	return t ? n ? yr.floatYSmallDelay1 ?? "" : yr.floatYSmall ?? "" : n ? yr.floatYDelay1 ?? "" : yr.floatY ?? "";
}
function Sr({ slots: e, itemWidth: t, gap: n = 0, isMobile: r = !1, direction: i = "left", className: a }) {
	let o = s(() => {
		if (e.length === 0) return 0;
		let t = Math.max(2, Math.ceil(br / e.length));
		return t % 2 == 0 ? t : t + 1;
	}, [e.length]), c = e.length * (t + n), l = Math.max(1.5, e.length * (r ? 2.5 : 8));
	return /* @__PURE__ */ p("div", {
		"aria-hidden": !0,
		className: W(yr.scroller, a),
		children: [
			/* @__PURE__ */ f("div", {
				className: W(yr.track, e.length > 0 && yr.trackAnimated),
				style: {
					gap: `${n}px`,
					"--los-idle-scroll-distance": `-${c}px`,
					"--los-idle-scroll-duration": `${l}s`,
					animationDirection: i === "left" ? "normal" : "reverse"
				},
				children: Array.from({ length: o }, (i, a) => /* @__PURE__ */ f("div", {
					className: yr.loop,
					style: { gap: `${n}px` },
					children: e.map((e, n) => /* @__PURE__ */ f("div", {
						className: W(yr.slot, xr(n, r)),
						style: {
							width: `${t}px`,
							height: `${t}px`
						},
						children: e
					}, `loop-${a}-${n}`))
				}, a))
			}),
			/* @__PURE__ */ f("div", { className: yr.fadeLeft }),
			/* @__PURE__ */ f("div", { className: yr.fadeRight })
		]
	});
}
var Cr = t(Sr), Z = {
	results: "_results_1of61_1",
	grid: "_grid_1of61_10",
	item: "_item_1of61_19",
	itemImage: "_itemImage_1of61_32",
	itemName: "_itemName_1of61_39",
	itemValue: "_itemValue_1of61_51",
	total: "_total_1of61_57",
	totalValue: "_totalValue_1of61_62",
	claimError: "_claimError_1of61_67",
	actions: "_actions_1of61_73",
	soldBack: "_soldBack_1of61_81",
	autoClaimLabel: "_autoClaimLabel_1of61_86",
	itemImageWrap: "_itemImageWrap_1of61_95",
	itemImageSubstitute: "_itemImageSubstitute_1of61_102",
	substituteBadge: "_substituteBadge_1of61_106",
	itemNotice: "_itemNotice_1of61_122",
	itemActions: "_itemActions_1of61_130",
	itemButton: "_itemButton_1of61_138",
	itemStatus: "_itemStatus_1of61_157",
	itemError: "_itemError_1of61_165"
};
//#endregion
//#region src/results/ResultsItemCard.tsx
function wr({ entry: e, claimed: t, action: n, onSell: r, onWithdraw: i }) {
	let { strings: a, currency: o } = B(), s = e.item.isGemSubstitute === !0, c = n?.status ?? "idle", l = c === "selling";
	return /* @__PURE__ */ p("div", {
		className: Z.item,
		children: [
			/* @__PURE__ */ p("div", {
				className: Z.itemImageWrap,
				children: [/* @__PURE__ */ f("img", {
					className: W(Z.itemImage, s && Z.itemImageSubstitute),
					src: e.item.imageUrl,
					alt: e.item.name,
					loading: "eager",
					decoding: "async"
				}), s ? /* @__PURE__ */ f("span", {
					className: Z.substituteBadge,
					children: a.substituteBadge
				}) : null]
			}),
			/* @__PURE__ */ f("p", {
				className: Z.itemName,
				children: e.item.name
			}),
			/* @__PURE__ */ f("span", {
				className: Z.itemValue,
				children: E(e.item.value, o, { compact: !0 })
			}),
			(() => {
				if (!t) return null;
				if (c === "withdrawn") return /* @__PURE__ */ f("p", {
					className: Z.itemStatus,
					children: a.withdrawRequested
				});
				if (c === "sold") return /* @__PURE__ */ p("p", {
					className: Z.itemStatus,
					children: [
						a.soldItemFor,
						" ",
						E(n?.soldCents ?? 0, o)
					]
				});
				let u = !!r && e.item.value > 0, m = !!i && !s;
				return !u && !m ? null : /* @__PURE__ */ p(d, { children: [
					!s && t.isLimited ? /* @__PURE__ */ f("p", {
						className: Z.itemNotice,
						children: a.limitedItemNotice
					}) : null,
					/* @__PURE__ */ p("div", {
						className: Z.itemActions,
						children: [u ? /* @__PURE__ */ f("button", {
							type: "button",
							className: Z.itemButton,
							disabled: l,
							onClick: () => r?.(t),
							children: l ? a.sellingItem : a.sellItem
						}) : null, m ? /* @__PURE__ */ f("button", {
							type: "button",
							className: Z.itemButton,
							disabled: l,
							onClick: () => i?.(t),
							children: a.withdrawItem
						}) : null]
					}),
					n?.errorMessage ? /* @__PURE__ */ f("p", {
						className: Z.itemError,
						children: n.errorMessage
					}) : null
				] });
			})()
		]
	});
}
//#endregion
//#region src/results/ResultsView.tsx
function Tr({ entries: e, claimStatus: t = "none", claimCostCents: n, claimErrorMessage: r, sellErrorMessage: i, soldCents: a, onClaim: o, onSellBack: c, onAutoClaimChange: l, onOpenAgain: u, claimedItems: m, itemActions: h, onSellItem: g, onWithdrawItem: _, buttonClassName: v, secondaryButtonClassName: y }) {
	let { strings: b, currency: x } = B(), S = s(() => [...e].sort((e, t) => t.item.value - e.item.value), [e]), C = s(() => new Map((m ?? []).map((e) => [e.openId, e])), [m]), w = s(() => e.reduce((e, t) => e + t.item.value, 0), [e]), T = t === "unclaimed" || t === "claiming" || t === "selling", D = t === "claiming" || t === "selling", O = !!c && w > 0, k = r ?? i ?? null;
	return /* @__PURE__ */ p("div", {
		className: Z.results,
		children: [
			/* @__PURE__ */ f("div", {
				className: Z.grid,
				children: S.map((e) => {
					let t = C.get(e.openId);
					return /* @__PURE__ */ f(wr, {
						entry: e,
						claimed: t,
						action: t ? h?.[t.inventoryItemId] : void 0,
						onSell: g,
						onWithdraw: _
					}, e.openId);
				})
			}),
			e.length > 1 && /* @__PURE__ */ p("div", {
				className: Z.total,
				children: [
					b.totalValue,
					":",
					" ",
					/* @__PURE__ */ f("span", {
						className: Z.totalValue,
						children: E(w, x)
					})
				]
			}),
			k ? /* @__PURE__ */ f("div", {
				className: Z.claimError,
				children: k
			}) : null,
			T ? /* @__PURE__ */ p(d, { children: [/* @__PURE__ */ p("div", {
				className: Z.actions,
				children: [/* @__PURE__ */ p("button", {
					type: "button",
					className: v,
					disabled: D,
					onClick: o,
					children: [t === "claiming" ? b.claiming : b.claim, n != null && t !== "claiming" ? /* @__PURE__ */ p(d, { children: [" ", E(n, x)] }) : null]
				}), O ? /* @__PURE__ */ p("button", {
					type: "button",
					className: y,
					disabled: D,
					onClick: c,
					children: [t === "selling" ? b.sellingBack : b.sellBack, t === "selling" ? null : /* @__PURE__ */ p(d, { children: [" ", E(w, x)] })]
				}) : null]
			}), l ? /* @__PURE__ */ p("label", {
				className: Z.autoClaimLabel,
				children: [/* @__PURE__ */ f("input", {
					type: "checkbox",
					defaultChecked: !1,
					onChange: (e) => l(e.target.checked)
				}), b.autoClaimLabel]
			}) : null] }) : /* @__PURE__ */ p(d, { children: [t === "sold" && a != null ? /* @__PURE__ */ p("div", {
				className: Z.soldBack,
				children: [
					b.soldBackFor,
					" ",
					/* @__PURE__ */ f("span", {
						className: Z.totalValue,
						children: E(a, x)
					})
				]
			}) : null, /* @__PURE__ */ f("button", {
				type: "button",
				className: v,
				onClick: u,
				children: b.openAgain
			})] })
		]
	});
}
var Q = {
	opener: "_opener_uqp0f_3",
	band: "_band_uqp0f_11",
	bandDesktop: "_bandDesktop_uqp0f_24",
	bandMobile: "_bandMobile_uqp0f_28",
	stage: "_stage_uqp0f_35",
	railLayer: "_railLayer_uqp0f_43",
	wheelArea: "_wheelArea_uqp0f_53",
	spinner: "_spinner_uqp0f_67",
	spinnerRow: "_spinnerRow_uqp0f_78",
	spinnerColumn: "_spinnerColumn_uqp0f_83",
	laneRow2: "_laneRow2_uqp0f_88",
	laneRowHorizontal: "_laneRowHorizontal_uqp0f_95",
	laneRowVertical: "_laneRowVertical_uqp0f_99",
	lane: "_lane_uqp0f_88",
	laneHorizontal: "_laneHorizontal_uqp0f_108",
	laneVertical: "_laneVertical_uqp0f_112",
	laneItems: "_laneItems_uqp0f_116",
	laneItemsHorizontal: "_laneItemsHorizontal_uqp0f_122",
	laneItemsVertical: "_laneItemsVertical_uqp0f_126",
	card: "_card_uqp0f_131",
	cardImage: "_cardImage_uqp0f_139",
	itemLabel: "_itemLabel_uqp0f_147",
	itemLabelHidden: "_itemLabelHidden_uqp0f_157",
	itemLabelInner: "_itemLabelInner_uqp0f_161",
	itemLabelValue: "_itemLabelValue_uqp0f_169",
	itemLabelName: "_itemLabelName_uqp0f_175",
	fade: "_fade_uqp0f_188",
	fadeLeft: "_fadeLeft_uqp0f_195",
	fadeRight: "_fadeRight_uqp0f_203",
	fadeTop: "_fadeTop_uqp0f_211",
	fadeBottom: "_fadeBottom_uqp0f_219",
	railRoot: "_railRoot_uqp0f_228",
	railSvg: "_railSvg_uqp0f_235",
	railTop: "_railTop_uqp0f_239",
	railBottom: "_railBottom_uqp0f_245",
	railLeft: "_railLeft_uqp0f_251",
	railRight: "_railRight_uqp0f_257"
};
//#endregion
//#region src/spinner/SpinnerWheel.tsx
function Er({ item: e }) {
	let { currency: t } = B();
	return /* @__PURE__ */ p("div", {
		className: Q.itemLabelInner,
		children: [/* @__PURE__ */ f("span", {
			className: Q.itemLabelValue,
			children: E(e.value, t, { compact: !0 })
		}), /* @__PURE__ */ f("p", {
			className: Q.itemLabelName,
			children: e.name
		})]
	});
}
function Dr({ index: e, getBoxItemsByIndex: t, animationPhase: n, setAnimationPhase: r, isRowOpen: i, geometry: a, timing: o, sounds: s, selectedOpenQuantity: c, isMobile: l, rarityRgb: u, selectedItemIndexRef: d, onRevealComplete: m, onItemSettled: g, renderItemLabel: _ }) {
	let { itemWidth: v, gap: y, containerWidth: b, containerHeight: x, startingOffset: S, endingOffset: C, spinSnapOffsets: w } = a, { animationDuration: T, spinStartDelaySec: E, staggerDelay: D, popupDuration: O } = o, { playTickSound: k, playRaritySound: A, lastTickTimeRef: j } = s, M = t(e), ee = () => {
		let t = M?.[35];
		t && (g?.(t.rarity, e), A(t.rarity));
	}, P = T + e * D, F = !l;
	return /* @__PURE__ */ f(h.div, {
		className: W(Q.lane, i ? Q.laneHorizontal : Q.laneVertical),
		"data-los-lane": e,
		initial: {
			transform: i ? `translateX(${S}px)` : `translateY(${S}px)`,
			opacity: 0
		},
		animate: n === "initialSpin" ? {
			transform: i ? `translateX(${C + (w[e] ?? 0)}px)` : `translateY(${C + (w[e] ?? 0)}px)`,
			opacity: 1
		} : {
			transform: i ? `translateX(${C}px)` : `translateY(${C}px)`,
			opacity: 1
		},
		transition: n === "initialSpin" ? {
			type: "tween",
			duration: T,
			delay: E,
			ease: [
				.25,
				.8,
				.5,
				1
			],
			opacity: {
				duration: .3,
				ease: "easeOut",
				delay: E
			}
		} : {
			type: "tween",
			duration: 300 / 1e3,
			ease: "easeOut",
			delay: e * D
		},
		style: { willChange: "transform, opacity" },
		onUpdate: (t) => {
			if (n !== "initialSpin" || !M || e !== 0) return;
			let r = Date.now();
			if (r - j.current < 16) return;
			j.current = r;
			let i = (t.transform ?? "").match(/translate[XY]\((-?\d+\.?\d*)px\)/);
			if (!i?.[1]) return;
			let a = Number.parseFloat(i[1]), o = l ? b / 2 : (c === 1 ? b : x) / 2, s = Math.round((o - a - v / 2) / (v + y));
			d.current !== s && s >= 0 && s < M.length && (d.current = s, k());
		},
		onAnimationComplete: () => {
			n === "initialSpin" ? r("snap") : n === "snap" && ee();
		},
		children: /* @__PURE__ */ f("div", {
			className: W(Q.laneItems, i ? Q.laneItemsHorizontal : Q.laneItemsVertical),
			style: { gap: `${y}px` },
			children: M?.map((t, n) => /* @__PURE__ */ p(h.div, {
				className: Q.card,
				style: {
					height: `${v}px`,
					width: `${v}px`,
					willChange: n === 35 ? "transform" : "transform, opacity"
				},
				animate: n === 35 ? { scale: [
					1,
					1.12,
					1.08
				] } : {
					scale: [1, .9],
					opacity: [1, .25]
				},
				transition: {
					type: "tween",
					duration: O,
					ease: "easeOut",
					delay: P
				},
				onAnimationComplete: () => {
					!F && n === 35 && e === c - 1 && m?.();
				},
				children: [/* @__PURE__ */ f(h.img, {
					src: t.imageUrl ?? "",
					alt: `${t.name}`,
					loading: "eager",
					decoding: "async",
					width: v,
					height: v,
					sizes: `${v}px`,
					className: Q.cardImage,
					style: {
						borderRadius: v < 90 ? "4px" : "8px",
						zIndex: 1,
						willChange: n === 35 ? "filter" : "auto"
					},
					initial: n === 35 ? { filter: N(u, t.rarity, "0px", "0") } : void 0,
					animate: n === 35 ? { filter: te(u, t.rarity) } : void 0,
					transition: n === 35 ? {
						duration: 300 / 1e3,
						ease: "easeOut",
						delay: P
					} : void 0
				}), F && /* @__PURE__ */ f(h.div, {
					className: W(Q.itemLabel, n !== 35 && Q.itemLabelHidden),
					initial: c > 1 ? {
						opacity: 0,
						y: -50
					} : void 0,
					animate: n === 35 ? {
						y: [-50, 0],
						opacity: [0, 1]
					} : {},
					transition: {
						type: "keyframes",
						duration: O,
						delay: P
					},
					onAnimationComplete: () => {
						e === c - 1 && n === 35 && m && m();
					},
					children: _ ? _(t) : /* @__PURE__ */ f(Er, { item: t })
				})]
			}, `spinner-${e}-item-${n}-${t.itemId ?? t.name}`))
		})
	});
}
//#endregion
//#region src/spinner/useSpinnerWheelItems.ts
function Or({ box: e, revealedItems: t, prandGenerator: n, itemWidth: r, gap: i, containerWidth: a, containerHeight: o, selectedOpenQuantity: c, isMobile: l }) {
	let u = s(() => t.slice(0, c).map((t) => Tt(e, 35, t, n)), [
		e,
		n,
		t,
		c
	]), d = (e) => u[e] ?? null, f = (e) => {
		switch (e) {
			case 2: return l ? { gap: 56 } : {
				marginLeft: "22%",
				marginRight: "22%"
			};
			case 3: return l ? { gap: 32 } : {
				marginLeft: "15%",
				marginRight: "15%"
			};
			case 4: return l ? { gap: 24 } : {
				marginLeft: "10%",
				marginRight: "10%"
			};
			case 5: return l ? { gap: 20 } : {
				marginLeft: "5%",
				marginRight: "5%"
			};
			default: return {};
		}
	}, p = s(() => {
		let e = [];
		for (let t = 0; t < c; t++) {
			let n = Math.floor(Math.random() * 2) === 0;
			e[t] = r / 4 * Math.random() * (n ? -1 : 1) + r / 4 * (n ? -1 : 1);
		}
		return e;
	}, [r, c]), { startingOffset: m, endingOffset: h } = s(() => {
		let e = l ? a / 2 : (c === 1 ? a : o) / 2, t = r * 4 + i * 3 - r / 2, n = r * 36 + i * 35 - r / 2;
		return {
			startingOffset: e - t,
			endingOffset: e - n
		};
	}, [
		c,
		a,
		o,
		r,
		i,
		l
	]);
	return {
		getBoxItemsByIndex: d,
		getMultiOpenSpacing: f,
		spinSnapOffsets: p,
		startingOffset: m,
		endingOffset: h
	};
}
//#endregion
//#region src/spinner/MotionSpinner.tsx
function kr({ className: e, gap: t = 0, box: n, isQuickReveal: r = !1, revealedItems: i, prandGenerator: a, itemWidth: o, containerWidth: u, containerHeight: m, selectedOpenQuantity: h, isMobile: g, sounds: _, rarityRgb: v, onRevealComplete: y, onItemSettled: b, renderItemLabel: x }) {
	let [S, C] = l("initialSpin"), w = h === 1 || g, T = c(null), E = c(0), D = s(() => r ? vt / 1e3 : _t / 1e3, [r]), O = s(() => r ? 150 / 1e3 : 250 / 1e3, [r]), { getBoxItemsByIndex: k, getMultiOpenSpacing: A, spinSnapOffsets: j, startingOffset: M, endingOffset: ee } = Or({
		box: n,
		revealedItems: i,
		prandGenerator: a,
		itemWidth: o,
		gap: t,
		containerWidth: u,
		containerHeight: m,
		selectedOpenQuantity: h,
		isMobile: g
	}), N = s(() => ({
		itemWidth: o,
		gap: t,
		containerWidth: u,
		containerHeight: m,
		startingOffset: M,
		endingOffset: ee,
		spinSnapOffsets: j
	}), [
		o,
		t,
		u,
		m,
		M,
		ee,
		j
	]), te = s(() => ({
		animationDuration: D,
		spinStartDelaySec: .25,
		staggerDelay: O,
		popupDuration: 1
	}), [D, O]), P = s(() => ({
		playTickSound: _.playTickSound,
		playRaritySound: _.playRaritySound,
		lastTickTimeRef: E
	}), [_.playTickSound, _.playRaritySound]);
	return /* @__PURE__ */ p("div", {
		className: W(Q.spinner, w ? Q.spinnerRow : Q.spinnerColumn, e),
		children: [k(0) && /* @__PURE__ */ f("div", {
			className: W(Q.laneRow2, g ? Q.laneRowHorizontal : Q.laneRowVertical),
			style: A(h),
			children: Array.from({ length: h }, (e, t) => /* @__PURE__ */ f(Dr, {
				index: t,
				getBoxItemsByIndex: k,
				animationPhase: S,
				setAnimationPhase: C,
				isRowOpen: w,
				geometry: N,
				timing: te,
				sounds: P,
				selectedOpenQuantity: h,
				isMobile: g,
				rarityRgb: v,
				selectedItemIndexRef: T,
				onRevealComplete: y,
				onItemSettled: b,
				renderItemLabel: x
			}, `spinner-${t}`))
		}), w ? /* @__PURE__ */ p(d, { children: [/* @__PURE__ */ f("div", { className: W(Q.fade, Q.fadeLeft) }), /* @__PURE__ */ f("div", { className: W(Q.fade, Q.fadeRight) })] }) : /* @__PURE__ */ p(d, { children: [/* @__PURE__ */ f("div", { className: W(Q.fade, Q.fadeTop) }), /* @__PURE__ */ f("div", { className: W(Q.fade, Q.fadeBottom) })] })]
	});
}
//#endregion
//#region src/spinner/SpinnerPointerRail.tsx
function Ar({ orientation: e, color: t, inset: n = 10, className: r }) {
	return e === "vertical" ? /* @__PURE__ */ p("div", {
		"aria-hidden": !0,
		className: W(Q.railRoot, r),
		children: [/* @__PURE__ */ f(Mr, {
			className: W(Q.railSvg, Q.railLeft),
			color: t,
			style: { left: n }
		}), /* @__PURE__ */ f(Mr, {
			className: W(Q.railSvg, Q.railRight),
			color: t,
			style: { right: n }
		})]
	}) : /* @__PURE__ */ p("div", {
		"aria-hidden": !0,
		className: W(Q.railRoot, r),
		children: [/* @__PURE__ */ f(jr, {
			className: W(Q.railSvg, Q.railTop),
			color: t,
			style: { top: n }
		}), /* @__PURE__ */ f(jr, {
			className: W(Q.railSvg, Q.railBottom),
			color: t,
			style: { bottom: n }
		})]
	});
}
function jr({ className: e, color: t, style: n }) {
	let r = Nr();
	return /* @__PURE__ */ p("svg", {
		"aria-hidden": "true",
		className: e,
		width: "74",
		height: "14",
		viewBox: "0 0 74 14",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		style: n,
		children: [/* @__PURE__ */ f("path", {
			d: "M0.00390625 13.3119L30.6914 13.5L36.6914 1.49988L42.7655 13.498M73.5039 13.3124L58.5039 13.3119L42.7655 13.498L36.6914 4.49988L30.6914 13.498",
			stroke: `url(#${r})`
		}), /* @__PURE__ */ f("defs", { children: /* @__PURE__ */ p("linearGradient", {
			id: r,
			x1: "74",
			y1: "7",
			x2: "0",
			y2: "7",
			gradientUnits: "userSpaceOnUse",
			children: [
				/* @__PURE__ */ f("stop", {
					stopColor: t,
					stopOpacity: "0"
				}),
				/* @__PURE__ */ f("stop", {
					offset: "0.5",
					stopColor: t
				}),
				/* @__PURE__ */ f("stop", {
					offset: "1",
					stopColor: t,
					stopOpacity: "0"
				})
			]
		}) })]
	});
}
function Mr({ className: e, color: t, style: n }) {
	let r = Nr();
	return /* @__PURE__ */ p("svg", {
		"aria-hidden": "true",
		className: e,
		width: "14",
		height: "74",
		viewBox: "0 0 14 74",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		style: n,
		children: [/* @__PURE__ */ f("path", {
			d: "M0.688111 0.00390625L0.5 30.6914L12.5001 36.6914L0.501953 42.7655M0.687616 73.5039L0.688111 58.5039L0.501953 42.7655L9.50012 36.6914L0.501953 30.6914",
			stroke: `url(#${r})`
		}), /* @__PURE__ */ f("defs", { children: /* @__PURE__ */ p("linearGradient", {
			id: r,
			x1: "7",
			y1: "0",
			x2: "7",
			y2: "74",
			gradientUnits: "userSpaceOnUse",
			children: [
				/* @__PURE__ */ f("stop", {
					stopColor: t,
					stopOpacity: "0"
				}),
				/* @__PURE__ */ f("stop", {
					offset: "0.5",
					stopColor: t
				}),
				/* @__PURE__ */ f("stop", {
					offset: "1",
					stopColor: t,
					stopOpacity: "0"
				})
			]
		}) })]
	});
}
function Nr() {
	return `los-spinner-rail-${a().replace(/:/g, "")}`;
}
//#endregion
//#region src/spinner/SpinnerOpener.tsx
var Pr = 768;
function Fr(e) {
	switch (e) {
		case 1: return 220;
		case 2: return 160;
		case 3: return 115;
		case 4: return 80;
		case 5: return 72;
		default: return 150;
	}
}
function Ir(e) {
	switch (e) {
		case 1: return 32;
		case 2: return 24;
		case 3: return 16;
		case 4: return 12;
		case 5: return 6;
		default: return 32;
	}
}
function Lr({ boxData: e, entries: t, quantity: r, isQuickReveal: a, prandGenerator: u, onRevealComplete: d }) {
	let { theme: m, sounds: h, renderItemLabel: g } = B(), _ = c(null), v = c(null), [y, b] = l(null), [x, S] = l(0), [C, w] = l(0);
	o(() => {
		let e = _.current;
		if (!e) return;
		let t = () => b(e.offsetWidth);
		if (t(), typeof ResizeObserver > "u") return;
		let n = new ResizeObserver(t);
		return n.observe(e), () => n.disconnect();
	}, []);
	let T = y === null ? null : y < Pr;
	o(() => {
		let e = v.current;
		if (!e) return;
		let t = () => {
			S(e.offsetWidth), w(e.offsetHeight);
		};
		if (t(), typeof ResizeObserver > "u") return;
		let n = new ResizeObserver(t);
		return n.observe(e), () => n.disconnect();
	}, [T]);
	let E = s(() => t.map((e) => e.item), [t]), D = s(() => t.length ? Math.max(...t.map((t) => le(t.item.value, e.priceCents ?? 0))) : null, [t, e.priceCents]), [O, k] = l(!1);
	i(() => {
		k(!1);
	}, [t]);
	let A = n((e) => {
		D !== null && e >= D && k(!0);
	}, [D]), j = O && D !== null ? P(m.railHex, D) : P(m.railHex, null), M = s(() => ({
		priceCents: e.priceCents,
		dropTable: e.dropTable
	}), [e.priceCents, e.dropTable]);
	return /* @__PURE__ */ f("div", {
		className: Q.opener,
		ref: _,
		children: T === null ? null : T ? /* @__PURE__ */ f("div", {
			className: W(Q.band, Q.bandMobile),
			children: /* @__PURE__ */ p("div", {
				className: Q.stage,
				children: [/* @__PURE__ */ f("div", {
					className: Q.railLayer,
					children: /* @__PURE__ */ f(Ar, {
						orientation: "horizontal",
						color: j,
						inset: -4
					})
				}), /* @__PURE__ */ f("div", {
					className: Q.wheelArea,
					ref: v,
					children: /* @__PURE__ */ f(kr, {
						gap: Ir(r),
						box: M,
						revealedItems: E,
						isQuickReveal: a,
						prandGenerator: u,
						itemWidth: Fr(r),
						containerWidth: x,
						containerHeight: C,
						selectedOpenQuantity: r,
						isMobile: !0,
						sounds: h,
						rarityRgb: m.rarityRgb,
						onRevealComplete: d,
						onItemSettled: A,
						renderItemLabel: g
					})
				})]
			})
		}) : /* @__PURE__ */ f("div", {
			className: W(Q.band, Q.bandDesktop),
			children: /* @__PURE__ */ p("div", {
				className: Q.stage,
				children: [/* @__PURE__ */ f("div", {
					className: Q.railLayer,
					children: /* @__PURE__ */ f(Ar, {
						orientation: r === 1 ? "horizontal" : "vertical",
						color: j
					})
				}), /* @__PURE__ */ f("div", {
					className: Q.wheelArea,
					ref: v,
					children: /* @__PURE__ */ f(kr, {
						gap: r > 1 ? 56 : 32,
						box: M,
						revealedItems: E,
						isQuickReveal: a,
						prandGenerator: u,
						itemWidth: 190,
						containerWidth: x,
						containerHeight: C,
						selectedOpenQuantity: r,
						isMobile: !1,
						sounds: h,
						rarityRgb: m.rarityRgb,
						onRevealComplete: d,
						onItemSettled: A,
						renderItemLabel: g
					})
				})]
			})
		})
	});
}
var $ = {
	root: "_root_4dzq4_5",
	phase: "_phase_4dzq4_28",
	losPhaseFadeIn: "_losPhaseFadeIn_4dzq4_1",
	idleHero: "_idleHero_4dzq4_51",
	boxImage: "_boxImage_4dzq4_59",
	boxName: "_boxName_4dzq4_66",
	boxPrice: "_boxPrice_4dzq4_73",
	idleScrollerArea: "_idleScrollerArea_4dzq4_79",
	idleCard: "_idleCard_4dzq4_87",
	idleCardImage: "_idleCardImage_4dzq4_96",
	controls: "_controls_4dzq4_105",
	controlsRow: "_controlsRow_4dzq4_114",
	quantityGroup: "_quantityGroup_4dzq4_123",
	quantityButton: "_quantityButton_4dzq4_135",
	quantityButtonActive: "_quantityButtonActive_4dzq4_151",
	primaryButton: "_primaryButton_4dzq4_156",
	secondaryButton: "_secondaryButton_4dzq4_185",
	toggleLabel: "_toggleLabel_4dzq4_206",
	priceTag: "_priceTag_4dzq4_215",
	revealArea: "_revealArea_4dzq4_222",
	revealStage: "_revealStage_4dzq4_231",
	statusRow: "_statusRow_4dzq4_240",
	demoBadge: "_demoBadge_4dzq4_248",
	errorBanner: "_errorBanner_4dzq4_261",
	loading: "_loading_4dzq4_272",
	fairnessArea: "_fairnessArea_4dzq4_281"
}, Rr = [
	1,
	2,
	3,
	4,
	5
];
function zr({ boxData: e, loading: t, machine: n, state: r, exit: i, maxQuantity: a, quickRevealMode: o, isDemoMode: c, errorMessage: l, claimErrorMessage: u, sellErrorMessage: m, prandGenerator: h, onOpenClick: g, onAutoClaimChange: _, onSellBack: v, itemActions: y, onSellItem: b, onWithdrawItem: x, renderIdle: S, renderResults: C, fairnessPanel: w, className: T, style: D }) {
	let { strings: O, theme: k, currency: A } = B(), j = Bn(r.phase), M = s(() => e ? e.dropTable.map((t) => {
		let n = le(t.value, e.priceCents ?? 0);
		return /* @__PURE__ */ f("div", {
			className: $.idleCard,
			children: /* @__PURE__ */ f("img", {
				className: $.idleCardImage,
				style: { filter: N(k.rarityRgb, n, "10px", "0.4") },
				src: t.imageUrl,
				alt: t.name,
				loading: "lazy",
				decoding: "async"
			})
		}, t.itemId);
	}) : [], [e, k.rarityRgb]), ee = r.phase === "requesting";
	return /* @__PURE__ */ p("div", {
		className: W("los-root", $.root, T),
		style: D,
		children: [/* @__PURE__ */ p("div", {
			className: $.phase,
			"data-los-phase": j,
			children: [
				j === "pre" && (t || !e ? /* @__PURE__ */ f("div", {
					className: $.loading,
					children: O.loading
				}) : S ? /* @__PURE__ */ f(d, { children: S({
					boxData: e,
					open: g
				}) }) : /* @__PURE__ */ p(d, { children: [
					/* @__PURE__ */ p("div", {
						className: $.idleHero,
						children: [
							e.imageUrl ? /* @__PURE__ */ f("img", {
								className: $.boxImage,
								src: e.imageUrl,
								alt: e.name,
								loading: "eager",
								decoding: "async"
							}) : null,
							/* @__PURE__ */ f("h2", {
								className: $.boxName,
								children: e.name
							}),
							/* @__PURE__ */ f("span", {
								className: $.boxPrice,
								children: E(e.priceCents, A)
							})
						]
					}),
					/* @__PURE__ */ f("div", {
						className: $.idleScrollerArea,
						children: /* @__PURE__ */ f(Cr, {
							slots: M,
							itemWidth: 104,
							gap: 12
						})
					}),
					/* @__PURE__ */ p("div", {
						className: $.controls,
						children: [l ? /* @__PURE__ */ f("div", {
							className: $.errorBanner,
							children: l
						}) : null, /* @__PURE__ */ p("div", {
							className: $.controlsRow,
							children: [
								/* @__PURE__ */ f("fieldset", {
									className: $.quantityGroup,
									"aria-label": O.quantity,
									children: Rr.filter((e) => e <= a).map((e) => /* @__PURE__ */ f("button", {
										type: "button",
										className: W($.quantityButton, e === r.quantity && $.quantityButtonActive),
										"aria-pressed": e === r.quantity,
										disabled: ee,
										onClick: () => n.setQuantity(e),
										children: e
									}, e))
								}),
								/* @__PURE__ */ p("button", {
									type: "button",
									className: $.primaryButton,
									disabled: ee,
									onClick: g,
									children: [
										O.open,
										"\xA0",
										/* @__PURE__ */ f("span", {
											className: $.priceTag,
											children: E(e.priceCents * r.quantity, A)
										})
									]
								}),
								o === "toggle" ? /* @__PURE__ */ p("label", {
									className: $.toggleLabel,
									children: [/* @__PURE__ */ f("input", {
										type: "checkbox",
										checked: r.isQuickReveal,
										onChange: (e) => n.setQuickReveal(e.target.checked)
									}), O.quickReveal]
								}) : null,
								c ? /* @__PURE__ */ f("span", {
									className: $.demoBadge,
									children: O.demoBadge
								}) : null
							]
						})]
					})
				] })),
				j === "during" && (e ? /* @__PURE__ */ p("div", {
					className: $.revealArea,
					children: [/* @__PURE__ */ f("div", {
						className: $.revealStage,
						children: /* @__PURE__ */ f(Lr, {
							boxData: e,
							entries: r.entries,
							quantity: Math.min(r.entries.length || r.quantity, a),
							isQuickReveal: r.isQuickReveal,
							prandGenerator: h,
							onRevealComplete: i.completeReveal
						})
					}), /* @__PURE__ */ f("button", {
						type: "button",
						className: $.secondaryButton,
						onClick: () => i.skipReveal(),
						children: O.skip
					})]
				}) : null),
				j === "post" && (() => {
					let e = () => n.reset();
					return C ? /* @__PURE__ */ f(d, { children: C({
						state: r,
						openAgain: e
					}) }) : /* @__PURE__ */ p(d, { children: [c ? /* @__PURE__ */ f("div", {
						className: $.statusRow,
						children: /* @__PURE__ */ f("span", {
							className: $.demoBadge,
							children: O.demoBadge
						})
					}) : null, /* @__PURE__ */ f(Tr, {
						entries: r.entries,
						claimStatus: r.claimStatus,
						claimCostCents: r.pendingClaim?.costCents ?? null,
						claimErrorMessage: u,
						sellErrorMessage: m,
						soldCents: r.sale?.soldCents ?? null,
						onClaim: () => void n.claim(),
						onSellBack: v,
						onAutoClaimChange: _,
						onOpenAgain: e,
						claimedItems: r.claimStatus === "claimed" ? r.claim?.items : void 0,
						itemActions: y,
						onSellItem: b,
						onWithdrawItem: x,
						buttonClassName: $.primaryButton,
						secondaryButtonClassName: $.secondaryButton
					})] });
				})()
			]
		}, j), w ? /* @__PURE__ */ f("div", {
			className: $.fairnessArea,
			children: w
		}) : null]
	});
}
//#endregion
//#region src/LootBoxOpener.tsx
var Br = 5;
function Vr(e, t) {
	return Kn(e) && e.code === "account_locked" ? t.errorAccountLocked : t.errorSellItemFailed;
}
function Hr(e) {
	let t = H(), r = e.publishableKey ?? t.publishableKey, a = e.baseUrl ?? t.baseUrl, o = e.sessionToken ?? t.sessionToken ?? null, u = e.sessionPending ?? t.sessionPending ?? !1, d = e.onSessionExpired ?? t.onSessionExpired, p = e.client ?? t.client, m = e.createClient ?? t.createClient, h = e.theme ?? t.theme, g = e.sounds ?? t.sounds, _ = e.soundBaseUrl ?? t.soundBaseUrl, v = e.locale ?? t.locale, y = e.currency ?? t.currency ?? C, b = s(() => A(v), [v]), w = s(() => re(h), [h]), T = se({
		sounds: g,
		soundBaseUrl: _
	}), { client: E, error: D } = vr({
		client: p,
		createClient: m,
		publishableKey: r,
		baseUrl: a,
		sessionToken: o,
		onSessionExpired: d
	}), { box: O, loading: k, error: j } = pr({
		client: E,
		boxId: e.boxId
	}), [M, ee] = ir({
		clientSeed: e.clientSeed,
		onClientSeedChange: e.onClientSeedChange
	}), N = !u && !o && !p, te = Math.max(1, Math.min(Br, e.maxQuantity ?? Br)), P = c({
		client: E,
		box: O,
		clientSeed: M,
		isDemoMode: N
	});
	P.current = {
		client: E,
		box: O,
		clientSeed: M,
		isDemoMode: N
	};
	let { mutateAsync: F } = x({
		mutationKey: [
			"loot-os",
			"open",
			Zn(E)
		],
		mutationFn: (e) => {
			let t = P.current;
			if (!t.box) throw Error("[loot-os] box not loaded yet");
			if (!t.client) throw Error("[loot-os] no API client configured");
			return t.client.openBox({
				boxId: t.box.id,
				clientSeed: t.clientSeed,
				amount: e.quantity,
				expectedVersion: t.box.version,
				idempotencyKey: e.idempotencyKey
			});
		}
	}, Jn()), I = n(async (e) => {
		let t = P.current;
		if (!t.box) throw Error("[loot-os] box not loaded yet");
		return t.isDemoMode ? lr(t.box, e, t.clientSeed) : F({
			quantity: e,
			idempotencyKey: Qn()
		});
	}, [F]), L = c(null), { mutateAsync: ne } = x({
		mutationKey: [
			"loot-os",
			"claim",
			Zn(E)
		],
		mutationFn: async (e) => {
			let t = P.current;
			if (!t.client) throw Error("[loot-os] no API client configured");
			let { claim: n } = await t.client.claimReveal({
				openGroupId: e.openGroupId,
				expectedPurchaseAmount: e.costCents
			});
			return n;
		}
	}, Jn()), R = c(0), z = n(async (e) => {
		if (P.current.isDemoMode) {
			let t = ur(e, L.current?.getState().entries ?? []);
			return R.current = t.balanceAfterCents, t;
		}
		return ne(e);
	}, [ne]), { mutateAsync: B } = x({
		mutationKey: [
			"loot-os",
			"sell-back",
			Zn(E)
		],
		mutationFn: async (e) => {
			let t = P.current;
			if (!t.client?.sellBackReveal) throw Error("[loot-os] the API client cannot sell back");
			let { sale: n } = await t.client.sellBackReveal({
				openGroupId: e.openGroupId,
				expectedPurchaseAmount: e.costCents
			});
			return n;
		}
	}, Jn()), V = n(async (e) => P.current.isDemoMode ? fr(e, L.current?.getState().entries ?? []) : B(e), [B]), ae = N || typeof E?.sellBackReveal == "function", [oe, U] = l({}), { mutateAsync: W } = x({
		mutationKey: [
			"loot-os",
			"sell-item",
			Zn(E)
		],
		mutationFn: async (e) => {
			let t = P.current;
			if (t.isDemoMode) {
				let t = dr(e, R.current);
				return R.current = t.balanceAfterCents, t;
			}
			if (!t.client?.sellInventoryItem) throw Error("[loot-os] the API client cannot sell one inventory item");
			return t.client.sellInventoryItem({ inventoryItemId: e.inventoryItemId });
		}
	}, Jn()), ce = N || typeof E?.sellInventoryItem == "function", G = c(e);
	G.current = e;
	let { machine: K, state: q } = gr({
		openBox: I,
		claimReveal: z,
		sellBackReveal: V,
		onBeforeOpen: n((e) => G.current.onOpenStart?.(e), []),
		onEvent: n((e) => {
			let t = G.current;
			switch (e.type) {
				case "open_start":
				case "restored":
				case "reset":
					U({});
					break;
				case "opened":
					t.onOpened?.(e.response);
					break;
				case "reveal_complete":
					t.onRevealComplete?.(e.response);
					break;
				case "claimed":
					t.onClaimed?.(e.claim);
					break;
				case "sold_back":
					t.onSoldBack?.(e.sale);
					break;
				case "claim_error":
				case "sell_back_error":
				case "error": Kn(e.error) && e.error.code === "insufficient_balance" && t.onInsufficientBalance?.(e.error), t.onError?.(e.error);
			}
		}, []),
		maxQuantity: te,
		defaultQuantity: e.defaultQuantity,
		defaultQuickReveal: e.quickReveal === !0
	});
	L.current = K;
	let [J, le] = l(0);
	i(() => {
		u && le((e) => e + 1);
	}, [u]);
	let ue = S({
		queryKey: [
			"loot-os",
			"unclaimedReveal",
			Zn(E),
			J
		],
		queryFn: async () => await E?.getUnclaimedReveal?.() ?? null,
		enabled: !u && !N && !!E?.getUnclaimedReveal,
		staleTime: 0,
		gcTime: 0
	}, Jn()), de = c(!1);
	i(() => {
		let e = ue.data;
		de.current || !e || (de.current = !0, K.restore(e));
	}, [ue.data, K]);
	let fe = u || ue.isLoading;
	i(() => {
		typeof e.quickReveal == "boolean" && K.setQuickReveal(e.quickReveal);
	}, [K, e.quickReveal]);
	let pe = s(() => Wn({
		revealExit: { lingerMs: Ct },
		isQuickReveal: q.isQuickReveal,
		actions: {
			finishReveal: () => K.completeReveal(),
			skipReveal: () => K.skip()
		}
	}), [K, q.isQuickReveal]);
	i(() => {
		q.phase !== "revealing" && pe.cancel();
	}, [q.phase, pe]), i(() => () => pe.cancel(), [pe]);
	let me = n(() => {
		T.unlock(), K.open();
	}, [K, T]), he = n(() => void K.sellBack(), [K]), ge = n((e) => {
		let t = e.inventoryItemId;
		U((e) => ({
			...e,
			[t]: {
				status: "selling",
				errorMessage: null
			}
		})), W(e).then((e) => {
			U((n) => ({
				...n,
				[t]: {
					status: "sold",
					soldCents: e.soldCents
				}
			}));
			try {
				G.current.onSold?.(e);
			} catch (e) {
				G.current.onError?.(e);
			}
		}, (e) => {
			U((n) => ({
				...n,
				[t]: {
					status: "idle",
					errorMessage: Vr(e, b)
				}
			})), G.current.onError?.(e);
		});
	}, [W, b]), _e = n((e) => {
		U((t) => ({
			...t,
			[e.inventoryItemId]: { status: "withdrawn" }
		})), G.current.onWithdrawRequested?.(e);
	}, []), Y = q.error ?? j ?? D, ve = Y ? Kn(Y) ? Y.code === "insufficient_balance" ? b.errorInsufficientBalance : Y.code === "account_locked" ? b.errorAccountLocked : Y.message || b.errorGeneric : b.errorGeneric : null, ye = q.claimError ? Kn(q.claimError) && q.claimError.code === "insufficient_balance" ? b.errorInsufficientBalance : Kn(q.claimError) && q.claimError.code === "account_locked" ? b.errorAccountLocked : b.errorClaimFailed : null, be = q.sellError ? Kn(q.sellError) && q.sellError.code === "insufficient_balance" ? b.errorInsufficientBalance : Kn(q.sellError) && q.sellError.code === "account_locked" ? b.errorAccountLocked : b.errorSellBackFailed : null, xe = !N && typeof e.onWithdrawRequested == "function", Se = !N && typeof E?.updateMySettings == "function", { mutate: Ce } = x({
		mutationKey: [
			"loot-os",
			"settings",
			Zn(E)
		],
		mutationFn: async (e) => await P.current.client?.updateMySettings?.(e) ?? null
	}, Jn()), we = n((e) => {
		Ce({ autoClaim: e });
	}, [Ce]), Te = s(() => ({
		strings: b,
		theme: w,
		sounds: T,
		currency: y,
		renderItemLabel: e.renderItemLabel
	}), [
		b,
		w,
		T,
		y,
		e.renderItemLabel
	]), Ee = e.showFairness ?? !0 ? /* @__PURE__ */ f(or, {
		client: E,
		lastResponse: q.lastResponse,
		clientSeed: M,
		onClientSeedChange: ee,
		locked: q.phase === "revealing" || q.phase === "requesting"
	}) : null, De = e.renderResults, Oe = De ? ({ state: e, openAgain: t }) => De({
		entries: e.entries,
		openAgain: t
	}) : void 0;
	return /* @__PURE__ */ f(ie.Provider, {
		value: Te,
		children: /* @__PURE__ */ f(zr, {
			boxData: O,
			loading: k || fe,
			machine: K,
			state: q,
			exit: pe,
			maxQuantity: te,
			quickRevealMode: e.quickReveal ?? !1,
			isDemoMode: N,
			errorMessage: ve,
			claimErrorMessage: ye,
			sellErrorMessage: be,
			onOpenClick: me,
			onSellBack: ae ? he : void 0,
			itemActions: oe,
			onSellItem: ce ? ge : void 0,
			onWithdrawItem: xe ? _e : void 0,
			onAutoClaimChange: Se ? we : void 0,
			renderIdle: e.renderIdle,
			renderResults: Oe,
			fairnessPanel: Ee,
			className: e.className,
			style: {
				...w.cssVars,
				...e.style
			}
		})
	});
}
//#endregion
//#region src/react/LootProvider.tsx
function Ur({ children: e, ...t }) {
	let n = s(() => t, [
		t.publishableKey,
		t.baseUrl,
		t.sessionToken,
		t.sessionPending,
		t.onSessionExpired,
		t.client,
		t.createClient,
		t.theme,
		t.sounds,
		t.soundBaseUrl,
		t.locale,
		t.currency
	]);
	return /* @__PURE__ */ f(V.Provider, {
		value: n,
		children: e
	});
}
//#endregion
export { on as BATTLE_OPENER_COMPACT_BREAKPOINT_PX, En as BATTLE_PLAYER_COUNTS, kn as BATTLE_REEL_TIMINGS, On as BATTLE_SETTLE_MS, Dn as BATTLE_SPIN_START_DELAY_MS, Tn as BATTLE_START_COUNTDOWN_MS, Fn as BattleOpener, In as BattleResultsGrid, zn as BattleStartingOverlay, $n as CLIENT_SEED_STORAGE_KEY, C as DEFAULT_CURRENCY, M as DEFAULT_RARITY_RAIL_HEX, j as DEFAULT_RARITY_RGB, k as DEFAULT_STRINGS, Hr as LootBoxOpener, Ur as LootProvider, er as MAX_CLIENT_SEED_LENGTH, _t as MOTION_ANIMATION_DURATION, St as MOTION_ENDING_ITEM_IDX, vt as MOTION_QUICK_ANIMATION_DURATION, bt as MOTION_QUICK_STAGGER_DELAY, Ct as MOTION_REVEAL_LINGER_MS, gt as MOTION_SECOND_PHASE_ANIMATION_DURATION, yt as MOTION_STAGGER_DELAY, xt as MOTION_STARTING_ITEM_IDX, or as ProvablyFairPanel, J as RESULT_RANGE, Hn as REVEAL_START_DELAY_MS, ae as SoundPlayer, Sn as battleOpenerLayout, Pn as battleReelGenerator, An as battleRevealIntervalMs, jn as battleRoundRevealMs, Mn as battleSpinItems, Un as createOpenerMachine, Wn as createOpenerRevealExit, w as currencyFractionDigits, D as currencyUnitName, Vn as defineOpenerDescriptor, E as formatAmount, O as formatAmountWithUnit, Tt as generateItemWheel, wt as generateRandomItems, ue as getItemChance, le as getItemRarity, Bn as getOpenerPhase, N as getRarityGlowFilter, te as getRarityGlowFilterByRarity, P as getSpinnerRailColor, Nn as hashSeed, Gn as isDemoOpenResponse, Kn as isLootApiErrorLike, xn as normalizeBattlePlayerCount, U as resolveSoundManifest, A as resolveStrings, re as resolveTheme, de as selectFromDropTable, ir as useClientSeed, pr as useLootBox, H as useLootProviderConfig, mr as useOpenBox, gr as useOpenerMachine, hr as useOpenerMachineState, se as useOpenerSounds };

//# sourceMappingURL=index.js.map